import React, {useCallback, useEffect, useMemo, useState} from 'react'
import {useNavigate, useSearchParams} from 'react-router-dom'
import {isPlatformSuperAdmin, useAuth} from '../../auth'
import Select, {type SingleValue, type StylesConfig} from 'react-select'
import {toast, ToastContainer} from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import './adminAccessRoleModals.scss'
import {getCompanyGroupAdminList, getManagementUsers} from '../core/_requests'
import type {ManagementUser} from '../core/_models'
import {
  createAdminAccessBundle,
  getAdminAccessBundles,
  getAdminAccessHistory,
  getAdminEffectivePreview,
  getAdminPermissionCatalog,
  getAdminUserBundles,
  getAdminUserScope,
  getAdminUserOverrides,
  updateAdminAccessBundle,
  updateAdminBundlePermissions,
  updateAdminUserBundles,
  updateAdminUserScope,
  updateAdminUserOverrides,
  type AccessGrantEffect,
  type AccessScope,
  type AdminAccessBundle,
  type AdminUserAccessOverride,
  type EffectiveAccessComparisonResult,
  type AuthChangeAudit,
  type PermissionCatalogEntry,
} from '../core/accessRequests'
import {
  copyRolePermissionsFrom,
  createCustomRoleWithPermissions,
  getRolePermissionsList,
  getRolesList,
  replaceRolePermissionsForRole,
  updateRole,
  type CreateRoleFullPayload,
  type RolePermissionRecord,
  type RoleRecord,
} from '../core/rolePermissionRequests'
import {
  buildSimpleOverrides,
  getModuleDef,
  mergeOverrideLists,
  OVERRIDE_MODULES,
  OVERRIDE_PRESETS,
  OVERRIDE_RECIPES,
  type OverrideModuleId,
  type OverridePresetId,
  type OverrideRecipe,
} from '../core/accessOverrideBuilder'
import {
  deriveDataViewingScope,
  getUserPrimaryLabel,
  getUserSecondaryLine,
  uniqueCompanyNamesFromUsers,
  uniqueRoleTypesFromUsers,
} from './adminAccessDerivedScope'
import {
  GroupedBundlePermissionEditor,
  GroupedEffectiveAccessPanel,
  GroupedPermissionCatalogReadonly,
  GroupedPermissionCheckboxGrid,
  GroupedUserOverrideEditor,
  type PreviewCatalogRow,
} from './GroupedPermissionViews'
import {resolveCatalogEntry} from '../core/permissionGrouping'
import {
  ACCESS_TEMPLATES,
  collectBundlePermissionCodes,
  collectCatalogCodesForFeatures,
  getAccessTemplate,
  mergeTemplateIntoFeatures,
  type AccessTemplateId,
  type SimpleWizardActionKey,
  type SimpleWizardModuleKey,
  roleScopeLabel,
  SIMPLE_ACTION_LABELS,
  SIMPLE_MODULE_HELP,
  SIMPLE_WIZARD_MODULES,
  suggestBundlesForTemplate,
} from '../core/simpleRoleWizardModel'

import {CreateRoleWizardBody} from './adminAccessCreateRoleWizard'
import {
  canManageRolePermissions,
  resolveActorCompanyGroupId,
  resolveActorCompanyAdminId,
  type RolePermissionActorContext,
} from '../core/rolePermissionHierarchy'
import {SimpleRolePermissionPanel} from './SimpleRolePermissionPanel'
import {
  buildSimpleMatrixBuckets,
  countModulesWithAnySelection,
  roleMatrixScopeSummary,
} from '../core/roleMatrixSimpleModel'
import SearchableSelect from '../../../shared/components/SearchableSelect'

type TabKey =
  | 'role-defaults'
  | 'registry'
  | 'bundle-master'
  | 'bundle-mapping'
  | 'user-bundles'
  | 'overrides'
  | 'scope'
  | 'preview'
  | 'history'
  | 'manage-roles'

type PrimarySectionId = 'roles' | 'users' | 'packages' | 'exceptions' | 'audit'

const PRIMARY_SECTION_ORDER: PrimarySectionId[] = ['roles', 'users', 'packages', 'exceptions', 'audit']

const PRIMARY_SECTION_META: Record<
  PrimarySectionId,
  {label: string; help: string; tabs: readonly TabKey[]}
> = {
  roles: {
    label: 'Roles',
    help: 'Roles define what a user can do.',
    tabs: ['manage-roles', 'role-defaults'],
  },
  users: {
    label: 'Users',
    help: 'Pick a person at the top, then assign access packages or review effective access preview.',
    tabs: ['user-bundles', 'preview'],
  },
  packages: {
    label: 'Access packages',
    help: 'Packages are reusable access templates.',
    tabs: ['bundle-master', 'bundle-mapping'],
  },
  exceptions: {
    label: 'Exceptions',
    help: 'Use exceptions only when one user needs special access.',
    tabs: ['overrides'],
  },
  audit: {
    label: 'Audit history',
    help: 'Track access changes.',
    tabs: ['history'],
  },
}

const ALL_PRIMARY_SECTION_TABS = new Set<TabKey>(
  PRIMARY_SECTION_ORDER.flatMap((id) => [...PRIMARY_SECTION_META[id].tabs])
)

const SUBTAB_LABELS: Partial<Record<TabKey, string>> = {
  'manage-roles': 'Manage roles',
  'role-defaults': 'Role permissions',
  'user-bundles': 'Assign access packages',
  preview: 'Effective access preview',
  'bundle-master': 'Package library',
  'bundle-mapping': 'Package templates',
  overrides: 'Access exceptions',
  history: 'Change log',
}

/**
 * Full advanced shortcut deck for platform super-admins (includes duplicates of primary tabs for quick jumps).
 * Company admins get a deduped, permission-filtered list — see {@link buildAdvancedToolDeck}.
 */
const ADVANCED_TOOL_DECK_FULL: readonly {tab: TabKey; label: string}[] = [
  {tab: 'registry', label: 'Permission catalog'},
  {tab: 'bundle-master', label: 'Package library'},
  {tab: 'bundle-mapping', label: 'Package templates'},
  {tab: 'role-defaults', label: 'Role permissions'},
  {tab: 'scope', label: 'Data visibility rules'},
  {tab: 'preview', label: 'Effective access preview'},
]

function buildAdvancedToolDeck(
  isPlatformSuperAdmin: boolean,
  hasPermission: (code: string) => boolean,
  hasAnyPermission: (codes: string[]) => boolean
): {tab: TabKey; label: string}[] {
  if (isPlatformSuperAdmin) return [...ADVANCED_TOOL_DECK_FULL]

  const deduped = ADVANCED_TOOL_DECK_FULL.filter((e) => !ALL_PRIMARY_SECTION_TABS.has(e.tab))
  return deduped.filter((e) => {
    if (e.tab === 'registry') return hasPermission('READ_PERMISSION')
    if (e.tab === 'scope') return hasAnyPermission(['READ_USER', 'UPDATE_ROLE_PERMISSION'])
    return true
  })
}

function primarySectionIdForTab(tab: TabKey): PrimarySectionId | null {
  for (const id of PRIMARY_SECTION_ORDER) {
    if (PRIMARY_SECTION_META[id].tabs.includes(tab)) return id
  }
  return null
}

function manageRolesRowType(r: RoleRecord): { label: string; badgeClass: string } {
  if (r.systemRole) {
    return { label: 'System / protected', badgeClass: 'badge badge-light-warning text-gray-800' }
  }
  if (r.customRole) {
    if (r.companyGroupAdminId != null) {
      return { label: 'Company custom', badgeClass: 'badge badge-light-primary text-gray-800' }
    }
    return { label: 'Global custom', badgeClass: 'badge badge-light-info text-gray-800' }
  }
  return { label: 'Global', badgeClass: 'badge badge-light text-gray-800' }
}

function manageRolesCompanyScopeLabel(r: RoleRecord): string {
  if (r.customRole && r.companyGroupAdminId != null) {
    return r.companyGroupName?.trim() || `Company Group #${r.companyGroupAdminId}`
  }
  return 'Global'
}

/** Every tab key (for URL validation). */
const ALL_TAB_KEYS: TabKey[] = [
  'role-defaults',
  'registry',
  'bundle-master',
  'bundle-mapping',
  'user-bundles',
  'overrides',
  'scope',
  'preview',
  'history',
  'manage-roles',
]

/** Old “Default packages by role” URLs → Role permissions */
const LEGACY_REMOVED_TAB_ALIASES: Record<string, TabKey> = {
  'default-packages': 'role-defaults',
  'package-defaults': 'role-defaults',
  'default-packages-by-role': 'role-defaults',
  'default-by-role': 'role-defaults',
}

const parseTabQuery = (raw: string | null): TabKey | null => {
  if (raw == null || raw.trim() === '') return null
  const key = raw.trim().toLowerCase().replace(/_/g, '-')
  const legacy = LEGACY_REMOVED_TAB_ALIASES[key]
  if (legacy) return legacy
  const aliases: Record<string, TabKey> = {
    roledefaults: 'role-defaults',
    rolematrix: 'role-defaults',
    matrix: 'role-defaults',
    bundles: 'user-bundles',
    assign: 'user-bundles',
    roles: 'manage-roles',
    'access-packages': 'bundle-master',
    exceptions: 'overrides',
    'user-overrides': 'overrides',
    overrides: 'overrides',
    audit: 'history',
    'audit-history': 'history',
  }
  const mapped = aliases[key]
  if (mapped) return mapped
  if (ALL_TAB_KEYS.includes(key as TabKey)) return key as TabKey
  return null
}

const SECTION_HELP: Partial<Record<TabKey, { title: string; body: string }>> = {
  'user-bundles': {
    title: 'Assign access packages',
    body: 'Turn on packages for this person, then save. Inactive packages cannot be selected.',
  },
  scope: {
    title: 'Data visibility rules',
    body: 'Configure maximum data visibility (ALL / COMPANY / VESSEL) for this user. Enforced on admin APIs when resource context is present. Non–super-admins cannot widen visibility beyond their own organization.',
  },
  'role-defaults': {
    title: 'Role permissions',
    body: 'Edit default permissions for the selected role. Start with Simple permissions; use Fine tune for grouped catalog rows and Advanced matrix when you need every section open at once. All views save the same role data.',
  },
  preview: {
    title: 'Effective access preview',
    body: 'Summary of what this person can do. After you change packages, use Refresh.',
  },
  history: {
    title: 'Audit history',
    body: 'Who changed this person’s access and when.',
  },
  registry: {
    title: 'Permission catalog',
    body: 'Reference list. You do not grant access here — use packages, role permissions, or access exceptions.',
  },
  'bundle-master': {
    title: 'Access packages',
    body: 'Create, rename, or turn packages off. Most sites already have standard packages.',
  },
  'bundle-mapping': {
    title: 'Package templates',
    body: 'Which permissions belong in each package. Often technical work; built-in packages may be locked.',
  },
  'manage-roles': {
    title: 'Roles',
    body: 'Roles define what a user can do. Create custom roles, edit metadata, open Role permissions to adjust defaults, duplicate a company role, or assign users. System roles stay read-only where your account requires it.',
  },
  overrides: {
    title: 'Access exceptions',
    body:
      'Per-user ALLOW or DENY on top of role defaults and packages. Use exceptions only when one user needs special access. Use the Role and Company filters above to find a person. Deny wins.',
  },
}

const TAB_LABELS: Record<TabKey, string> = {
  'role-defaults': 'Role permissions',
  'user-bundles': 'Assign access packages',
  scope: 'Data visibility rules',
  preview: 'Effective access preview',
  history: 'Audit history',
  registry: 'Permission catalog',
  'bundle-master': 'Access packages',
  'bundle-mapping': 'Package templates',
  'manage-roles': 'Roles',
  overrides: 'Access exceptions',
}

/** Templates for custom roles: exclude SUPER_ADMIN (and CUSTOM, which is identity-only). */
const BEHAVIOR_TEMPLATE_OPTIONS = [
  'COMPANY_GROUP_ADMIN',
  'COMPANY_ADMIN',
  'CREW',
  'SHORE',
  'OPERATOR',
  'VENDOR',
] as const

const DATA_SCOPE_OPTIONS = ['ALL', 'COMPANY', 'VESSEL'] as const

type BundleFormState = {
  id?: number
  code: string
  name: string
  moduleHint: string
  active: boolean
}

const initialBundleForm: BundleFormState = {
  code: '',
  name: '',
  moduleHint: 'ADMIN',
  active: true,
}

const prettyDate = (value?: string | null): string => {
  if (!value) return '-'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return date.toLocaleString()
}

const normalizeCode = (value: string): string => value.trim().toUpperCase()

type UserPickerOption = {
  value: number
  label: string
  user: ManagementUser
}

const userSelectStyles: StylesConfig<UserPickerOption, false> = {
  control: (base) => ({
    ...base,
    minHeight: 48,
    borderColor: '#ced4da',
    fontSize: '0.95rem',
  }),
  menu: (base) => ({ ...base, zIndex: 20 }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isFocused ? '#f5f8fa' : '#fff',
    color: '#3f4254',
  }),
}

const AdminAccessManagement: React.FC = () => {
  const navigate = useNavigate()
  const {currentUser, auth, hasPermission, hasAnyPermission} = useAuth()
  const isPlatformSuperAdminUser = isPlatformSuperAdmin(currentUser, auth)

  /** Resolved tenant for company-scoped custom role UI (matches backend `getCompanyGroupAdminScopeId`). */
  const actorCompanyGroupId = useMemo(
    () => resolveActorCompanyGroupId(currentUser as Record<string, unknown> | null | undefined),
    [currentUser]
  )
  const actorEffectiveBehavior = useMemo(() => {
    const u = currentUser as Record<string, unknown> | null | undefined
    const role = u?.role as {roleType?: string; baseRoleType?: string; effectiveBehaviorType?: string} | undefined
    return (
      role?.effectiveBehaviorType ||
      role?.baseRoleType ||
      role?.roleType ||
      (u?.roleType as string | undefined) ||
      null
    )
  }, [currentUser])
  const actorPermissionContext = useMemo<RolePermissionActorContext>(
    () => ({
      isSuperAdmin: isPlatformSuperAdminUser,
      actorRoleId: Number((currentUser as any)?.role?.id ?? (currentUser as any)?.roleId ?? 0) || null,
      actorCompanyGroupId,
      actorCompanyAdminId: resolveActorCompanyAdminId(currentUser as Record<string, unknown> | null | undefined),
      actorEffectiveBehavior,
      hasUpdateRolePermission: hasPermission('UPDATE_ROLE_PERMISSION'),
    }),
    [actorCompanyGroupId, actorEffectiveBehavior, currentUser, hasPermission, isPlatformSuperAdminUser]
  )

  const canCreateCustomRole = isPlatformSuperAdminUser || actorCompanyGroupId != null

  const canEditRoleCatalogRow = (r: RoleRecord) => canManageRolePermissions(r, actorPermissionContext)

  const canEditSelectedCustomMatrix = (r: RoleRecord | null) =>
    canManageRolePermissions(r, actorPermissionContext)



  const advancedToolDeckItems = useMemo(
    () => buildAdvancedToolDeck(isPlatformSuperAdminUser, hasPermission, hasAnyPermission),
    [isPlatformSuperAdminUser, hasPermission, hasAnyPermission]
  )

  const showAdvancedToolsSection = isPlatformSuperAdminUser || advancedToolDeckItems.length > 0

  const [searchParams, setSearchParams] = useSearchParams()
  const userIdFromQuery = useMemo(() => {
    const raw = searchParams.get('userId')
    if (raw == null || raw === '') return null
    const n = Number(raw)
    return Number.isFinite(n) && n > 0 ? n : null
  }, [searchParams])

  useEffect(() => {
    if (process.env.NODE_ENV !== 'development') return
    const role = currentUser?.role
    console.debug('[RolePermissions] auth role snapshot', {
      currentUser,
      currentUserRole: currentUser?.role,
      currentUserRoleType: (currentUser as {roleType?: string} | undefined)?.roleType,
      role,
      roleRoleType: role?.roleType,
      'role.name': role?.name,
      roleName: role?.roleName,
      roleCode: role?.code,
      auth,
      authRole: auth?.role,
    })
  }, [currentUser, auth])

  const [activeTab, setActiveTab] = useState<TabKey>('manage-roles')
  const [loading, setLoading] = useState(false)
  const [advancedToolsExpanded, setAdvancedToolsExpanded] = useState(false)

  const setTab = useCallback(
    (key: TabKey) => {
      setActiveTab(key)
      if (ALL_PRIMARY_SECTION_TABS.has(key)) {
        setAdvancedToolsExpanded(false)
      } else {
        setAdvancedToolsExpanded(true)
      }
      const next = new URLSearchParams(searchParams)
      next.set('tab', key)
      setSearchParams(next, { replace: true })
    },
    [searchParams, setSearchParams]
  )

  const primarySectionActive = primarySectionIdForTab(activeTab)
  const advancedToolsActive = !ALL_PRIMARY_SECTION_TABS.has(activeTab)

  const [permissionCatalog, setPermissionCatalog] = useState<PermissionCatalogEntry[]>([])
  const [permissionSearch, setPermissionSearch] = useState('')
  const [previewSearch, setPreviewSearch] = useState('')
  const [bundleMapSearch, setBundleMapSearch] = useState('')
  const [packageSetupShowAllCatalog, setPackageSetupShowAllCatalog] = useState(false)
  const [overrideSearch, setOverrideSearch] = useState('')

  const [bundles, setBundles] = useState<AdminAccessBundle[]>([])
  const [selectedBundleId, setSelectedBundleId] = useState<number | null>(null)
  const [bundleForm, setBundleForm] = useState<BundleFormState>(initialBundleForm)
  const [bundleCodeFilter, setBundleCodeFilter] = useState('')

  const [users, setUsers] = useState<ManagementUser[]>([])
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null)
  /** Narrow the list before opening the searchable select */
  const [pickerRoleFilter, setPickerRoleFilter] = useState<string>('ALL')
  const [pickerCompanyFilter, setPickerCompanyFilter] = useState<string>('ALL')

  const [assignedBundleIds, setAssignedBundleIds] = useState<number[]>([])
  const [userOverrides, setUserOverrides] = useState<AdminUserAccessOverride[]>([])
  const [preview, setPreview] = useState<EffectiveAccessComparisonResult | null>(null)
  const [history, setHistory] = useState<AuthChangeAudit[]>([])
  const [historyLimit, setHistoryLimit] = useState(100)

  const [rolesList, setRolesList] = useState<RoleRecord[]>([])
  const editableRoleTargets = useMemo(
    () => rolesList.filter((role) => canEditRoleCatalogRow(role)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [actorPermissionContext, rolesList]
  )
  const [rolePermissionsList, setRolePermissionsList] = useState<RolePermissionRecord[]>([])
  const [roleMatrixRoleId, setRoleMatrixRoleId] = useState<number | null>(null)
  const [roleDefaultDraft, setRoleDefaultDraft] = useState<Set<string>>(new Set())
  const [roleMatrixSearch, setRoleMatrixSearch] = useState('')
  const [roleMatrixUiMode, setRoleMatrixUiMode] = useState<'simple' | 'fine' | 'advanced'>('simple')

  const [createRoleOpen, setCreateRoleOpen] = useState(false)
  const [createRoleStep, setCreateRoleStep] = useState<1 | 2 | 3 | 4>(1)
  const [createRoleBusy, setCreateRoleBusy] = useState(false)
  const [crAccessTemplateId, setCrAccessTemplateId] = useState<AccessTemplateId>('custom')
  const [crModuleFeatures, setCrModuleFeatures] = useState(() => mergeTemplateIntoFeatures(getAccessTemplate('custom')))
  const [crWizardBundleId, setCrWizardBundleId] = useState<number | null>(null)
  const [crWizardExtraCodes, setCrWizardExtraCodes] = useState<Set<string>>(() => new Set())
  const [crShowAdvancedCreateFields, setCrShowAdvancedCreateFields] = useState(false)
  const [crShowFineTuneExtras, setCrShowFineTuneExtras] = useState(false)
  const [crPresetCopyFromId, setCrPresetCopyFromId] = useState<number | null>(null)
  const [viewRoleRow, setViewRoleRow] = useState<RoleRecord | null>(null)
  const [editMrShowAdvanced, setEditMrShowAdvanced] = useState(false)
  const [crDisplayName, setCrDisplayName] = useState('')
  const [crCode, setCrCode] = useState('')
  const [crBehaviorTemplate, setCrBehaviorTemplate] = useState<string>('COMPANY_ADMIN')
  const [crDescription, setCrDescription] = useState('')
  const [crScope, setCrScope] = useState<string>('COMPANY')
  const [crActive, setCrActive] = useState(true)
  const [crPermMode, setCrPermMode] = useState<'BLANK' | 'COPY' | 'MANUAL'>('BLANK')
  const [crCopyFromId, setCrCopyFromId] = useState<string>('')
  const [crWizardPermSearch, setCrWizardPermSearch] = useState('')
  const [crManualSelected, setCrManualSelected] = useState<Set<string>>(() => new Set())
  /** Superadmin: null = global role; number = company group admin id */
  const [crCompanyGroupAdminId, setCrCompanyGroupAdminId] = useState<number | null>(null)
  const [crCompanyGroupOptions, setCrCompanyGroupOptions] = useState<{id: number; label: string}[]>([])
  const [crCompanyGroupLoadError, setCrCompanyGroupLoadError] = useState<string | null>(null)

  const [editManageRole, setEditManageRole] = useState<RoleRecord | null>(null)
  const [editMrName, setEditMrName] = useState('')
  const [editMrDesc, setEditMrDesc] = useState('')
  const [editMrCode, setEditMrCode] = useState('')
  const [editMrBehavior, setEditMrBehavior] = useState('COMPANY_ADMIN')
  const [editMrScope, setEditMrScope] = useState<string>('COMPANY')
  const [editMrActive, setEditMrActive] = useState(true)
  const [editMrBusy, setEditMrBusy] = useState(false)

  const [copyPermsOpen, setCopyPermsOpen] = useState(false)
  const [copyPermsTarget, setCopyPermsTarget] = useState<RoleRecord | null>(null)
  const [copyPermsSourceId, setCopyPermsSourceId] = useState('')
  const [copyPermsBusy, setCopyPermsBusy] = useState(false)

  const [scopeEdit, setScopeEdit] = useState<{
    hardMaxScope: AccessScope
    companyId: string
    vesselId: string
  } | null>(null)

  const [overrideUiMode, setOverrideUiMode] = useState<'simple' | 'advanced'>('simple')
  const [simpleModule, setSimpleModule] = useState<OverrideModuleId>('admin_surface')
  const [simpleFeature, setSimpleFeature] = useState<string>('ALL')
  const [simplePreset, setSimplePreset] = useState<OverridePresetId>('READ_ONLY')
  const [simpleReason, setSimpleReason] = useState('')
  const [simpleScope, setSimpleScope] = useState<AccessScope | ''>('')

  const bundleCodeById = useMemo(() => {
    const m = new Map<number, string>()
    bundles.forEach((b) => m.set(b.id, b.code))
    return m
  }, [bundles])

  const selectedBundle = useMemo(
    () => bundles.find((bundle) => bundle.id === selectedBundleId) || null,
    [bundles, selectedBundleId]
  )

  const selectedUser = useMemo(
    () => users.find((u) => Number(u.userId) === selectedUserId) || null,
    [users, selectedUserId]
  )

  const assignedPackageCount = assignedBundleIds.length

  const derivedDataScope = useMemo(() => deriveDataViewingScope(selectedUser), [selectedUser])

  const roleTypesForFilter = useMemo(() => uniqueRoleTypesFromUsers(users), [users])
  const companyNamesForFilter = useMemo(() => uniqueCompanyNamesFromUsers(users), [users])

  const usersMatchingPickerFilters = useMemo(() => {
    return users.filter((u) => {
      if (pickerRoleFilter !== 'ALL') {
        const ur = (u.roleType || u.roleName || '').trim()
        if (ur !== pickerRoleFilter) return false
      }
      if (pickerCompanyFilter !== 'ALL') {
        const cn = (u.companyName || '').trim()
        if (pickerCompanyFilter === '__NO_COMPANY__') {
          if (cn) return false
        } else if (cn !== pickerCompanyFilter) {
          return false
        }
      }
      return true
    })
  }, [users, pickerRoleFilter, pickerCompanyFilter])

  const userPickerOptions: UserPickerOption[] = useMemo(
    () =>
      usersMatchingPickerFilters.map((u) => ({
        value: u.userId,
        label: getUserPrimaryLabel(u),
        user: u,
      })),
    [usersMatchingPickerFilters]
  )

  const selectedUserPickerOption: UserPickerOption | null = useMemo(() => {
    if (selectedUserId == null) return null
    const fromList = userPickerOptions.find((o) => o.value === selectedUserId)
    if (fromList) return fromList
    if (selectedUser && Number(selectedUser.userId) === selectedUserId) {
      return {
        value: selectedUser.userId,
        label: getUserPrimaryLabel(selectedUser),
        user: selectedUser,
      }
    }
    return null
  }, [userPickerOptions, selectedUserId, selectedUser])

  const sectionHelp = SECTION_HELP[activeTab]

  const permissionCodesOrdered = useMemo(
    () => permissionCatalog.map((e) => resolveCatalogEntry(e).permissionCode),
    [permissionCatalog]
  )

  const catalogByCode = useMemo(() => {
    const m = new Map<string, PermissionCatalogEntry>()
    for (const e of permissionCatalog) {
      const r = resolveCatalogEntry(e)
      m.set(r.permissionCode, r)
    }
    return m
  }, [permissionCatalog])

  const selectedMatrixRole = useMemo(
    () => editableRoleTargets.find((r) => r.id === roleMatrixRoleId) ?? null,
    [editableRoleTargets, roleMatrixRoleId]
  )

  const permissionCountByRoleId = useMemo(() => {
    const m = new Map<number, number>()
    rolePermissionsList.forEach((rp) => {
      const rid = Number(rp.roleId)
      if (!Number.isFinite(rid)) return
      m.set(rid, (m.get(rid) ?? 0) + 1)
    })
    return m
  }, [rolePermissionsList])

  const usersCountByRoleId = useMemo(() => {
    const m = new Map<number, number>()
    users.forEach((u) => {
      const rid = u.roleId
      if (rid == null || !Number.isFinite(Number(rid))) return
      const id = Number(rid)
      m.set(id, (m.get(id) ?? 0) + 1)
    })
    return m
  }, [users])

  const selectedAccessTemplate = useMemo(() => getAccessTemplate(crAccessTemplateId), [crAccessTemplateId])

  const suggestedWizardBundles = useMemo(
    () => suggestBundlesForTemplate(bundles, selectedAccessTemplate, 8),
    [bundles, selectedAccessTemplate]
  )

  const wizardSimpleCodes = useMemo(() => {
    const fromFeat = collectCatalogCodesForFeatures(permissionCatalog, crModuleFeatures)
    const b = bundles.find((x) => x.id === crWizardBundleId)
    const fromB = collectBundlePermissionCodes(b)
    return new Set([...fromFeat, ...fromB])
  }, [permissionCatalog, crModuleFeatures, crWizardBundleId, bundles])

  const wizardSubmitCodes = useMemo(() => {
    const s = new Set(wizardSimpleCodes)
    crWizardExtraCodes.forEach((c) => s.add(normalizeCode(c)))
    return s
  }, [wizardSimpleCodes, crWizardExtraCodes])

  const customMatrixLocked = Boolean(
    selectedMatrixRole && !canEditSelectedCustomMatrix(selectedMatrixRole)
  )

  const roleMatrixSimpleBuckets = useMemo(
    () => buildSimpleMatrixBuckets(permissionCatalog, roleMatrixSearch),
    [permissionCatalog, roleMatrixSearch]
  )

  const roleMatrixModulesWithSelection = useMemo(
    () => countModulesWithAnySelection(roleMatrixSimpleBuckets, roleDefaultDraft),
    [roleMatrixSimpleBuckets, roleDefaultDraft]
  )

  const roleMatrixUiDisabled = Boolean(
    customMatrixLocked
  )

  const filteredBundles = useMemo(() => {
    const query = bundleCodeFilter.trim().toUpperCase()
    if (!query) return bundles
    return bundles.filter(
      (bundle) =>
        bundle.code.toUpperCase().includes(query) || bundle.name.toUpperCase().includes(query)
    )
  }, [bundles, bundleCodeFilter])

  const simpleBuilderPreview = useMemo(
    () =>
      buildSimpleOverrides(permissionCodesOrdered, simpleModule, simpleFeature, simplePreset, {
        reason: simpleReason,
        scope: simpleScope ? simpleScope : null,
      }),
    [permissionCodesOrdered, simpleModule, simpleFeature, simplePreset, simpleReason, simpleScope]
  )

  const simpleModuleFeatures = useMemo(
    () => getModuleDef(simpleModule)?.features ?? [],
    [simpleModule]
  )

  const mappedPermissionByCode = useMemo(() => {
    if (!selectedBundle) return new Map<string, {effect: AccessGrantEffect; defaultScope?: AccessScope | null}>()
    const out = new Map<string, {effect: AccessGrantEffect; defaultScope?: AccessScope | null}>()
    selectedBundle.permissions.forEach((row) => {
      out.set(normalizeCode(row.permissionCode), {
        effect: row.effect || 'ALLOW',
        defaultScope: row.defaultScope || null,
      })
    })
    return out
  }, [selectedBundle])

  const bundlePackageEditCatalog = useMemo(() => {
    if (!selectedBundle) return permissionCatalog
    if (selectedBundle.systemDefined || packageSetupShowAllCatalog) {
      return permissionCatalog
    }
    const inPackage = new Set(mappedPermissionByCode.keys())
    return permissionCatalog.filter((e) => inPackage.has(resolveCatalogEntry(e).permissionCode))
  }, [selectedBundle, packageSetupShowAllCatalog, permissionCatalog, mappedPermissionByCode])

  const bundlePackageCountMode: 'full' | 'scoped' =
    Boolean(selectedBundle && !selectedBundle.systemDefined && !packageSetupShowAllCatalog)
      ? 'scoped'
      : 'full'

  const bundlePackageEmptyMessage =
    !selectedBundle
      ? 'Choose a package above.'
      : selectedBundle.systemDefined
        ? 'Built-in packages cannot be edited here.'
        : packageSetupShowAllCatalog
          ? 'No permissions match your search.'
          : mappedPermissionByCode.size === 0
            ? 'This package has no permissions yet. Enable “Show all permissions to add” to assign permissions from the catalog.'
            : 'No permissions in this package match your search. Try clearing the filter or turn on “Show all permissions to add”.'

  const loadBase = async () => {
    setLoading(true)
    try {
      const [catalog, accessBundles, managementUsers, rps, roles] = await Promise.all([
        getAdminPermissionCatalog(),
        getAdminAccessBundles(),
        getManagementUsers(),
        getRolePermissionsList(),
        getRolesList(),
      ])
      setPermissionCatalog(catalog)
      setBundles(accessBundles)
      setUsers(managementUsers)
      setRolePermissionsList(rps)
      setRolesList(roles)

      setSelectedBundleId((prev) => {
        if (prev != null) return prev
        if (accessBundles.length > 0) {
          return accessBundles[0].id
        }
        return null
      })

      setSelectedUserId((prev) => {
        if (userIdFromQuery != null) {
          if (managementUsers.some((u) => Number(u.userId) === userIdFromQuery)) {
            return userIdFromQuery
          }
          toast.warn(
            `User id ${userIdFromQuery} is not in your management scope. Pick a user in Target User below.`
          )
        }
        if (prev != null && managementUsers.some((u) => Number(u.userId) === prev)) {
          return prev
        }
        if (managementUsers.length > 0) {
          return Number(managementUsers[0].userId)
        }
        return null
      })
    } catch (error: any) {
      toast.error('Failed to load Admin access data')
    } finally {
      setLoading(false)
    }
  }

  const loadUserSections = async (userId: number) => {
    setLoading(true)
    try {
      const [bundleAssignments, overrides, previewResult, historyRows, scopeBinding] = await Promise.all([
        getAdminUserBundles(userId),
        getAdminUserOverrides(userId),
        getAdminEffectivePreview(userId),
        getAdminAccessHistory(userId, historyLimit),
        getAdminUserScope(userId),
      ])

      setAssignedBundleIds(
        bundleAssignments
          .filter((row) => row.active)
          .map((row) => Number(row.bundleId))
      )
      setUserOverrides(
        overrides.map((row) => ({
          permissionCode: row.permissionCode,
          effect: row.effect,
          scope: row.scope || null,
          reason: row.reason || '',
        }))
      )
      setPreview(previewResult)
      setHistory(historyRows)
      setScopeEdit({
        hardMaxScope: scopeBinding.hardMaxScope ?? 'COMPANY',
        companyId: scopeBinding.companyId != null ? String(scopeBinding.companyId) : '',
        vesselId: scopeBinding.vesselId != null ? String(scopeBinding.vesselId) : '',
      })
    } catch (error: any) {
      toast.error('Failed to load user access details')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadBase()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (userIdFromQuery == null) return
    if (users.length === 0) return
    if (users.some((u) => Number(u.userId) === userIdFromQuery)) {
      setSelectedUserId((prev) => (prev === userIdFromQuery ? prev : userIdFromQuery))
    }
  }, [userIdFromQuery, users])

  useEffect(() => {
    if (selectedUserId != null) {
      loadUserSections(selectedUserId)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedUserId])

  useEffect(() => {
    const raw = searchParams.get('tab')
    const parsed = parseTabQuery(raw)
    if (parsed) {
      setActiveTab(parsed)
      setAdvancedToolsExpanded(!ALL_PRIMARY_SECTION_TABS.has(parsed))
      const key = raw?.trim().toLowerCase().replace(/_/g, '-') ?? ''
      if (LEGACY_REMOVED_TAB_ALIASES[key]) {
        const next = new URLSearchParams(searchParams)
        next.set('tab', parsed)
        setSearchParams(next, { replace: true })
      }
      return
    }
    const fallback: TabKey = userIdFromQuery != null ? 'user-bundles' : 'manage-roles'
    const next = new URLSearchParams(searchParams)
    next.set('tab', fallback)
    setSearchParams(next, { replace: true })
    setActiveTab(fallback)
    setAdvancedToolsExpanded(!ALL_PRIMARY_SECTION_TABS.has(fallback))
  }, [searchParams, setSearchParams, userIdFromQuery])

  useEffect(() => {
    if (editableRoleTargets.length === 0) {
      setRoleMatrixRoleId(null)
      return
    }
    setRoleMatrixRoleId((prev) => {
      if (prev != null && editableRoleTargets.some((r) => r.id === prev)) return prev
      return editableRoleTargets[0].id
    })
  }, [editableRoleTargets])

  useEffect(() => {
    if (roleMatrixRoleId == null) {
      setRoleDefaultDraft(new Set())
      return
    }
    const enabled = new Set<string>()
    rolePermissionsList.forEach((rp) => {
      if (Number(rp.roleId) !== roleMatrixRoleId) return
      const name = rp.permission?.name
      if (!name) return
      enabled.add(normalizeCode(name))
    })
    setRoleDefaultDraft(enabled)
  }, [roleMatrixRoleId, rolePermissionsList])

  useEffect(() => {
    if (selectedUserId == null) {
      setScopeEdit(null)
    }
  }, [selectedUserId])

  useEffect(() => {
    setPackageSetupShowAllCatalog(false)
  }, [selectedBundleId])

  const reloadRolePermissionsOnly = async () => {
    setLoading(true)
    try {
      const rps = await getRolePermissionsList()
      setRolePermissionsList(rps)
      toast.success('Role permissions reloaded.')
    } catch (error: any) {
      toast.error('Failed to reload role permissions')
    } finally {
      setLoading(false)
    }
  }

  const refreshRolesAndPermissions = async (selectRoleId?: number) => {
    setLoading(true)
    try {
      const [rps, roles] = await Promise.all([getRolePermissionsList(), getRolesList()])
      setRolePermissionsList(rps)
      setRolesList(roles)
      if (selectRoleId != null && Number.isFinite(selectRoleId)) {
        setRoleMatrixRoleId(selectRoleId)
      }
    } catch (error: any) {
      toast.error('Failed to refresh roles')
    } finally {
      setLoading(false)
    }
  }

  const openCreateRoleModal = () => {
    if (!canCreateCustomRole) return
    setCreateRoleStep(1)
    setCrAccessTemplateId('custom')
    setCrModuleFeatures(mergeTemplateIntoFeatures(getAccessTemplate('custom')))
    setCrWizardBundleId(null)
    setCrWizardExtraCodes(new Set())
    setCrShowAdvancedCreateFields(false)
    setCrShowFineTuneExtras(false)
    setCrPresetCopyFromId(null)
    setCrDisplayName('')
    setCrCode('')
    setCrBehaviorTemplate('COMPANY_ADMIN')
    setCrDescription('')
    setCrScope('COMPANY')
    setCrActive(true)
    setCrPermMode('BLANK')
    setCrCopyFromId('')
    setCrWizardPermSearch('')
    setCrManualSelected(new Set())
    setCrCompanyGroupAdminId(null)
    setCreateRoleOpen(true)
  }

  const openDuplicateRoleWizard = (r: RoleRecord) => {
    if (!canCreateCustomRole || !r.customRole) return
    openCreateRoleModal()
    setCrDisplayName(`Copy of ${(r.displayName || r.roleType || 'Role').replace(/_/g, ' ').trim()}`)
    setCrPresetCopyFromId(r.id)
    setCrDescription((r.description || '').trim())
    setCreateRoleStep(1)
  }

  const pickAccessTemplate = (id: AccessTemplateId) => {
    setCrAccessTemplateId(id)
    const t = getAccessTemplate(id)
    if (t) {
      setCrBehaviorTemplate(t.baseRoleType)
      setCrScope(t.defaultDataScope)
      setCrModuleFeatures(mergeTemplateIntoFeatures(t))
      setCrWizardBundleId(null)
      setCrWizardExtraCodes(new Set())
    }
  }

  const toggleCrModuleFeature = (mk: SimpleWizardModuleKey, ak: SimpleWizardActionKey) => {
    setCrModuleFeatures((prev) => ({
      ...prev,
      [mk]: {...prev[mk], [ak]: !prev[mk][ak]},
    }))
  }

  const toggleCrWizardExtraPermission = (code: string) => {
    const k = normalizeCode(code)
    setCrWizardExtraCodes((prev) => {
      const next = new Set(prev)
      if (next.has(k)) next.delete(k)
      else next.add(k)
      return next
    })
  }

  const setCrWizardExtraMany = useCallback((codes: string[], selected: boolean) => {
    setCrWizardExtraCodes((prev) => {
      const next = new Set(prev)
      for (const c of codes) {
        const k = normalizeCode(c)
        if (selected) next.add(k)
        else next.delete(k)
      }
      return next
    })
  }, [])

  useEffect(() => {
    if (!createRoleOpen || !isPlatformSuperAdminUser) {
      setCrCompanyGroupOptions([])
      setCrCompanyGroupLoadError(null)
      return
    }
    let cancelled = false
    setCrCompanyGroupLoadError(null)
    ;(async () => {
      try {
        const list = await getCompanyGroupAdminList()
        if (cancelled) return
        const opts = list
          .filter((row) => row.isActive !== false && row.active !== false)
          .map((row) => {
            const id = Number(row.id)
            const label = (row.name || row.companyGroupAdminDetails || `Company group #${id}`).trim()
            return {id, label}
          })
          .filter((o) => Number.isFinite(o.id))
        opts.sort((a, b) => a.label.localeCompare(b.label))
        setCrCompanyGroupOptions(opts)
      } catch (e: any) {
        if (!cancelled) {
          setCrCompanyGroupOptions([])
          setCrCompanyGroupLoadError('Could not load company groups.')
        }
      }
    })()
    return () => {
      cancelled = true
    }
  }, [createRoleOpen, isPlatformSuperAdminUser])

  const goCreateRoleNext = () => {
    const name = crDisplayName.trim()
    if (createRoleStep === 1) {
      if (!name) {
        toast.error('Role name is required.')
        return
      }
      if (isPlatformSuperAdminUser && crCompanyGroupAdminId == null) {
        toast.error('Company group is required for custom role creation.')
        return
      }
      if (!isPlatformSuperAdminUser && actorCompanyGroupId == null) {
        toast.error('You do not have a company scope to create custom roles.')
        return
      }
      setCreateRoleStep(2)
      return
    }
    if (createRoleStep === 2) {
      if (!crAccessTemplateId) {
        toast.error('Choose an access template.')
        return
      }
      setCreateRoleStep(3)
      return
    }
    if (createRoleStep === 3) {
      setCreateRoleStep(4)
    }
  }

  const goCreateRoleBack = () => {
    if (createRoleStep <= 1) return
    setCreateRoleStep((s) => (s - 1) as 1 | 2 | 3 | 4)
  }

  const toggleCrWizardPermission = (code: string) => {
    const key = normalizeCode(code)
    setCrManualSelected((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  const setRoleMatrixMany = useCallback((codes: string[], selected: boolean) => {
    setRoleDefaultDraft((prev) => {
      const next = new Set(prev)
      for (const c of codes) {
        const key = normalizeCode(c)
        if (selected) next.add(key)
        else next.delete(key)
      }
      return next
    })
  }, [])

  const setCrManualMany = useCallback((codes: string[], selected: boolean) => {
    setCrManualSelected((prev) => {
      const next = new Set(prev)
      for (const c of codes) {
        const key = normalizeCode(c)
        if (selected) next.add(key)
        else next.delete(key)
      }
      return next
    })
  }, [])

  const setBundlePermissionsMany = useCallback((codes: string[], enabled: boolean) => {
    setBundles((prev) => {
      const bundleId = selectedBundle?.id
      if (bundleId == null || selectedBundle?.systemDefined) return prev
      return prev.map((bundle) => {
        if (bundle.id !== bundleId) return bundle
        const draft = new Map<string, {effect: AccessGrantEffect; defaultScope?: AccessScope | null}>()
        bundle.permissions.forEach((row) => {
          draft.set(normalizeCode(row.permissionCode), {
            effect: row.effect || 'ALLOW',
            defaultScope: row.defaultScope || null,
          })
        })
        for (const c of codes) {
          const key = normalizeCode(c)
          if (!enabled) draft.delete(key)
          else if (!draft.has(key)) draft.set(key, {effect: 'ALLOW', defaultScope: null})
        }
        const updatedPermissions = Array.from(draft.entries()).map(([code, value]) => ({
          permissionCode: code,
          effect: value.effect,
          defaultScope: value.defaultScope || null,
        }))
        return {...bundle, permissions: updatedPermissions}
      })
    })
  }, [selectedBundle?.id, selectedBundle?.systemDefined])

  const userOverrideModuleBulk = useMemo(
    () => ({
      getEffect: (it: {effect: AccessGrantEffect}) => it.effect,
      disabled: loading || selectedUserId == null,
      onAllowAllVisibleInModule: (items: {index: number}[]) => {
        const idx = new Set(items.map((i) => i.index))
        setUserOverrides((prev) => prev.map((row, i) => (idx.has(i) ? {...row, effect: 'ALLOW' as const} : row)))
      },
      onClearAllowInModule: (items: {index: number; effect: AccessGrantEffect}[]) => {
        const idx = new Set(items.filter((i) => i.effect === 'ALLOW').map((i) => i.index))
        setUserOverrides((prev) => prev.map((row, i) => (idx.has(i) ? {...row, effect: 'DENY' as const} : row)))
      },
      onDenyAllVisibleInModule: (items: {index: number}[]) => {
        const idx = new Set(items.map((i) => i.index))
        setUserOverrides((prev) => prev.map((row, i) => (idx.has(i) ? {...row, effect: 'DENY' as const} : row)))
      },
      onClearDenyInModule: (items: {index: number; effect: AccessGrantEffect}[]) => {
        const idx = new Set(items.filter((i) => i.effect === 'DENY').map((i) => i.index))
        setUserOverrides((prev) => prev.map((row, i) => (idx.has(i) ? {...row, effect: 'ALLOW' as const} : row)))
      },
    }),
    [loading, selectedUserId]
  )

  const submitCreateRoleFinal = async (options?: {assignUsersAfter?: boolean}) => {
    if (!canCreateCustomRole) {
      toast.error('You do not have a company scope to create custom roles.')
      return
    }
    const name = crDisplayName.trim()
    if (!name) {
      toast.error('Role name is required.')
      return
    }
    if (crShowAdvancedCreateFields && crPermMode === 'COPY' && !crCopyFromId && crPresetCopyFromId == null) {
      toast.error('Select a role to copy from.')
      return
    }

    const advancedOpen = crShowAdvancedCreateFields
    const useDuplicateCopy = crPresetCopyFromId != null
    const useAdvancedCopy = advancedOpen && crPermMode === 'COPY' && Boolean(crCopyFromId)
    const useAdvancedManual = advancedOpen && crPermMode === 'MANUAL'
    const useAdvancedBlank = advancedOpen && crPermMode === 'BLANK'

    let permissionMode: CreateRoleFullPayload['permissionMode']
    let copyFromRoleId: number | undefined
    let permissionNames: string[] | undefined

    if (useDuplicateCopy) {
      permissionMode = 'COPY'
      copyFromRoleId = crPresetCopyFromId!
    } else if (useAdvancedCopy) {
      permissionMode = 'COPY'
      copyFromRoleId = Number(crCopyFromId)
    } else if (useAdvancedBlank) {
      permissionMode = 'BLANK'
    } else if (useAdvancedManual) {
      permissionMode = 'MANUAL'
      permissionNames = Array.from(crManualSelected).map((c) => normalizeCode(c))
    } else if (crAccessTemplateId === 'blank' && wizardSubmitCodes.size === 0) {
      permissionMode = 'BLANK'
    } else {
      permissionMode = 'MANUAL'
      permissionNames = Array.from(wizardSubmitCodes)
    }

    if (
      permissionMode === 'MANUAL' &&
      (!permissionNames || permissionNames.length === 0) &&
      !useAdvancedManual
    ) {
      if (
        !window.confirm(
          'This role will be created with no permissions yet. Continue? You can add them later under Role permissions.'
        )
      ) {
        return
      }
    }

    setCreateRoleBusy(true)
    try {
      const autoCode = crCode.trim() || undefined
      const payload: CreateRoleFullPayload = {
        displayName: name,
        code: autoCode,
        baseRoleType: crBehaviorTemplate,
        description: crDescription.trim() || undefined,
        defaultDataScope: crScope || undefined,
        active: crActive,
        permissionMode,
        copyFromRoleId,
        permissionNames,
        ...(isPlatformSuperAdminUser ? {companyGroupAdminId: crCompanyGroupAdminId} : {}),
      }
      const created = await createCustomRoleWithPermissions(payload)
      toast.success(`Role created: ${created.displayName || created.code} (#${created.id})`)
      setCreateRoleOpen(false)
      setCreateRoleStep(1)
      await refreshRolesAndPermissions(created.id)
      if (options?.assignUsersAfter) {
        navigate(`/manage/usermanagement?openAddUser=1&prefillRoleId=${created.id}`)
      }
    } catch (error: any) {
      toast.error('Create role failed')
    } finally {
      setCreateRoleBusy(false)
    }
  }

  const openEditManageRole = (r: RoleRecord) => {
    if (!canEditRoleCatalogRow(r)) return
    setEditManageRole(r)
    setEditMrShowAdvanced(false)
    setEditMrName((r.displayName || r.roleType || '').trim())
    setEditMrDesc((r.description || '').trim())
    setEditMrCode((r.code || '').trim())
    setEditMrBehavior((r.baseRoleType || 'COMPANY_ADMIN').trim())
    setEditMrScope((r.defaultDataScope || 'COMPANY').toString().trim() || 'COMPANY')
    setEditMrActive(r.active !== false)
  }

  const goAssignUsersForRole = (r: RoleRecord) => {
    navigate(`/manage/usermanagement?openAddUser=1&prefillRoleId=${r.id}`)
  }

  const openViewRoleRow = (r: RoleRecord) => {
    setViewRoleRow(r)
  }

  const goManagePermissionsForRole = (r: RoleRecord) => {
    setRoleMatrixRoleId(r.id)
    setRoleMatrixUiMode('simple')
    setTab('role-defaults')
  }

  const goTopAssignUserToRole = () => {
    navigate('/manage/usermanagement?openAddUser=1')
  }

  const openTopManagePermissions = () => {
    const rid = roleMatrixRoleId ?? editableRoleTargets[0]?.id ?? null
    if (rid == null) {
      toast.info('No editable lower roles are available here yet.')
      return
    }
    setRoleMatrixRoleId(rid)
    setRoleMatrixUiMode('simple')
    setTab('role-defaults')
  }

  const openTopDuplicateRole = () => {
    const r = rolesList.find((x) => x.customRole && canEditRoleCatalogRow(x))
    if (!r) {
      toast.info('No company custom role is available to duplicate here. Pick a row below and use Duplicate.')
      return
    }
    openDuplicateRoleWizard(r)
  }

  const openCopyPermissionsModal = (r: RoleRecord) => {
    if (!canEditRoleCatalogRow(r)) return
    setCopyPermsTarget(r)
    setCopyPermsSourceId('')
    setCopyPermsOpen(true)
  }

  const submitCopyPermissions = async () => {
    if (!copyPermsTarget || !copyPermsSourceId) {
      toast.error('Select a source role.')
      return
    }
    const targetId = copyPermsTarget.id
    const sourceId = Number(copyPermsSourceId)
    if (!Number.isFinite(sourceId) || sourceId === targetId) {
      toast.error('Choose a different source role.')
      return
    }
    if (
      !window.confirm(
        `Replace all permissions and menu ACL rows for "${copyPermsTarget.displayName || copyPermsTarget.code}" with a copy from the selected role? This cannot be undone from this dialog.`
      )
    ) {
      return
    }
    setCopyPermsBusy(true)
    try {
      await copyRolePermissionsFrom(targetId, sourceId)
      toast.success('Permissions copied.')
      setCopyPermsOpen(false)
      setCopyPermsTarget(null)
      await refreshRolesAndPermissions(targetId)
    } catch (error: any) {
      toast.error('Copy failed')
    } finally {
      setCopyPermsBusy(false)
    }
  }

  const submitEditManageRole = async () => {
    if (!editManageRole || !canEditRoleCatalogRow(editManageRole)) {
      toast.error('You cannot edit this role.')
      return
    }
    const dn = editMrName.trim()
    if (!dn) {
      toast.error('Display name is required.')
      return
    }
    if (editManageRole.customRole) {
      const code = editMrCode.trim()
      if (!code) {
        toast.error('Role code is required for custom roles.')
        return
      }
      if (!editMrBehavior.trim()) {
        toast.error('Behavior template is required.')
        return
      }
    }
    setEditMrBusy(true)
    try {
      await updateRole(editManageRole.id, {
        displayName: dn,
        description: editMrDesc.trim() || null,
        active: editManageRole.systemRole ? null : editMrActive,
        ...(editManageRole.customRole
          ? {
              code: editMrCode.trim(),
              baseRoleType: editMrBehavior.trim(),
              defaultDataScope: editMrScope.trim() || null,
            }
          : {}),
      })
      toast.success('Role updated.')
      setEditManageRole(null)
      await refreshRolesAndPermissions(editManageRole.id)
    } catch (error: any) {
      toast.error('Update failed')
    } finally {
      setEditMrBusy(false)
    }
  }

  const toggleRoleMatrixPermission = (code: string) => {
    const key = normalizeCode(code)
    setRoleDefaultDraft((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  const saveRoleMatrix = async () => {
    if (roleMatrixRoleId == null) return
    if (roleMatrixUiDisabled) {
      toast.error(
        customMatrixLocked
          ? 'You cannot change permissions for this custom role (global or another company).'
          : 'This protected system role is read-only for your account.'
      )
      return
    }
    const byCode = new Set<string>()
    rolePermissionsList.forEach((rp) => {
      if (Number(rp.roleId) !== roleMatrixRoleId) return
      const name = rp.permission?.name
      if (!name) return
      byCode.add(normalizeCode(name))
    })
    const draft = new Set(Array.from(roleDefaultDraft).map((c) => normalizeCode(c)))
    const same =
      byCode.size === draft.size && Array.from(byCode).every((c) => draft.has(c))
    if (same) {
      toast.info('No changes to save.')
      return
    }
    const codes = Array.from(draft)
    setLoading(true)
    try {
      await replaceRolePermissionsForRole(roleMatrixRoleId, codes)
      const rps = await getRolePermissionsList()
      setRolePermissionsList(rps)
      toast.success('Role permissions saved.')
      window.dispatchEvent(new CustomEvent('rbac-permissions-updated', {detail: {roleId: roleMatrixRoleId}}))
    } catch (error: any) {
      toast.error('Failed to save role permissions')
    } finally {
      setLoading(false)
    }
  }

  const saveUserScopeBinding = async () => {
    if (selectedUserId == null || scopeEdit == null) return
    setLoading(true)
    try {
      await updateAdminUserScope(selectedUserId, {
        hardMaxScope: scopeEdit.hardMaxScope,
        companyId: scopeEdit.companyId.trim() ? Number(scopeEdit.companyId) : null,
        vesselId: scopeEdit.vesselId.trim() ? Number(scopeEdit.vesselId) : null,
        source: 'ADMIN_UI',
      })
      toast.success('Data visibility saved.')
      await loadUserSections(selectedUserId)
    } catch (error: any) {
      toast.error('Failed to save data visibility')
    } finally {
      setLoading(false)
    }
  }

  const applyOverrideRecipe = (recipe: OverrideRecipe) => {
    setOverrideUiMode('simple')
    setSimpleModule(recipe.moduleId)
    setSimpleFeature(recipe.featureId)
    setSimplePreset(recipe.presetId)
    toast.info(`Loaded “${recipe.label}”. Review the preview, then add to the list.`)
  }

  const changeSimpleModule = (id: OverrideModuleId) => {
    setSimpleModule(id)
    const m = getModuleDef(id)
    setSimpleFeature((prev) => (m?.features.some((f) => f.id === prev) ? prev : 'ALL'))
  }

  const addSimpleOverridesToList = () => {
    const { overrides } = buildSimpleOverrides(permissionCodesOrdered, simpleModule, simpleFeature, simplePreset, {
      reason: simpleReason,
      scope: simpleScope ? simpleScope : null,
    })
    if (overrides.length === 0) {
      toast.warn(
        'No access exceptions were generated. The catalog may not list permissions for this module yet — check the effective access preview or use the advanced editor.'
      )
      return
    }
    setUserOverrides((prev) => mergeOverrideLists(prev, overrides))
    toast.success(`Added ${overrides.length} access exception row(s). Save when ready.`)
  }

  const resetBundleForm = () => setBundleForm(initialBundleForm)

  const submitBundle = async () => {
    if (!bundleForm.code.trim() || !bundleForm.name.trim()) {
      toast.error('Enter a short code and a display name for the package.')
      return
    }
    setLoading(true)
    try {
      if (bundleForm.id) {
        await updateAdminAccessBundle(bundleForm.id, {
          code: normalizeCode(bundleForm.code),
          name: bundleForm.name.trim(),
          moduleHint: bundleForm.moduleHint.trim() || null,
          active: bundleForm.active,
        })
        toast.success('Access package updated.')
      } else {
        await createAdminAccessBundle({
          code: normalizeCode(bundleForm.code),
          name: bundleForm.name.trim(),
          moduleHint: bundleForm.moduleHint.trim() || null,
          active: bundleForm.active,
        })
        toast.success('New access package created.')
      }
      resetBundleForm()
      await loadBase()
    } catch (error: any) {
      toast.error('Failed to save bundle')
    } finally {
      setLoading(false)
    }
  }

  const editBundle = (bundle: AdminAccessBundle) => {
    setBundleForm({
      id: bundle.id,
      code: bundle.code,
      name: bundle.name,
      moduleHint: bundle.moduleHint || '',
      active: bundle.active,
    })
    setSelectedBundleId(bundle.id)
    setTab('bundle-master')
  }

  const deactivateBundle = async (bundle: AdminAccessBundle) => {
    if (bundle.systemDefined) return
    setLoading(true)
    try {
      await updateAdminAccessBundle(bundle.id, {
        code: bundle.code,
        name: bundle.name,
        moduleHint: bundle.moduleHint || null,
        active: false,
      })
      toast.success(`Package “${bundle.code}” is turned off.`)
      await loadBase()
    } catch (error: any) {
      toast.error('Failed to deactivate bundle')
    } finally {
      setLoading(false)
    }
  }

  const saveBundlePermissionMapping = async () => {
    if (!selectedBundle) {
      toast.error('Choose an access package first.')
      return
    }
    if (selectedBundle.systemDefined) {
      toast.error('Built-in packages cannot be changed here. Use a custom package, or create a new package.')
      return
    }

    const included = new Set<string>()
    const payload: {
      permissionCode: string
      effect: AccessGrantEffect
      defaultScope: AccessScope | null
    }[] = []
    for (const code of permissionCodesOrdered) {
      if (!mappedPermissionByCode.has(code)) continue
      const key = normalizeCode(code)
      if (included.has(key)) continue
      included.add(key)
      const row = mappedPermissionByCode.get(code)!
      payload.push({
        permissionCode: key,
        effect: row.effect,
        defaultScope: row.defaultScope || null,
      })
    }

    setLoading(true)
    try {
      await updateAdminBundlePermissions(selectedBundle.id, payload)
        toast.success('Package contents saved.')
      await loadBase()
    } catch (error: any) {
      toast.error('Failed to update bundle mapping')
    } finally {
      setLoading(false)
    }
  }

  const togglePermissionInBundle = (permissionCode: string, enabled: boolean) => {
    if (!selectedBundle || selectedBundle.systemDefined) return
    const key = normalizeCode(permissionCode)
    const draft = new Map(mappedPermissionByCode)
    if (!enabled) {
      draft.delete(key)
    } else if (!draft.has(key)) {
      draft.set(key, {effect: 'ALLOW', defaultScope: null})
    }
    const updatedPermissions = Array.from(draft.entries()).map(([code, value]) => ({
      permissionCode: code,
      effect: value.effect,
      defaultScope: value.defaultScope || null,
    }))
    setBundles((prev) =>
      prev.map((bundle) =>
        bundle.id === selectedBundle.id
          ? {...bundle, permissions: updatedPermissions}
          : bundle
      )
    )
  }

  const setPermissionEffect = (permissionCode: string, effect: AccessGrantEffect) => {
    if (!selectedBundle || selectedBundle.systemDefined) return
    const key = normalizeCode(permissionCode)
    setBundles((prev) =>
      prev.map((bundle) => {
        if (bundle.id !== selectedBundle.id) return bundle
        const updated = bundle.permissions.map((row) =>
          normalizeCode(row.permissionCode) === key ? {...row, effect} : row
        )
        return {...bundle, permissions: updated}
      })
    )
  }

  const setPermissionScope = (permissionCode: string, scopeValue: '' | AccessScope) => {
    if (!selectedBundle || selectedBundle.systemDefined) return
    const key = normalizeCode(permissionCode)
    setBundles((prev) =>
      prev.map((bundle) => {
        if (bundle.id !== selectedBundle.id) return bundle
        const updated = bundle.permissions.map((row) =>
          normalizeCode(row.permissionCode) === key
            ? {...row, defaultScope: scopeValue || null}
            : row
        )
        return {...bundle, permissions: updated}
      })
    )
  }

  const saveUserBundles = async () => {
    if (!selectedUserId) {
      toast.error('Select a person at the top of the page first.')
      return
    }
    setLoading(true)
    try {
      await updateAdminUserBundles(selectedUserId, Array.from(new Set(assignedBundleIds)))
      toast.success('Access packages saved for this person.')
      await loadUserSections(selectedUserId)
    } catch (error: any) {
      toast.error('Failed to save user bundle assignments')
    } finally {
      setLoading(false)
    }
  }

  const saveUserOverrides = async () => {
    if (!selectedUserId) {
      toast.error('Select a person at the top of the page first.')
      return
    }
    const validRows = userOverrides
      .map((row) => ({
        permissionCode: normalizeCode(row.permissionCode || ''),
        effect: row.effect,
        scope: row.scope || null,
        reason: row.reason?.trim() || null,
      }))
      .filter((row) => row.permissionCode)
    if (validRows.length !== userOverrides.filter((row) => row.permissionCode?.trim()).length) {
      toast.error('Fix invalid access exception rows before saving.')
      return
    }
    setLoading(true)
    try {
      await updateAdminUserOverrides(selectedUserId, validRows)
      toast.success('Access exceptions saved.')
      await loadUserSections(selectedUserId)
    } catch (error: any) {
      toast.error('Failed to update access exceptions')
    } finally {
      setLoading(false)
    }
  }

  const refreshPreview = async () => {
    if (!selectedUserId) return
    setLoading(true)
    try {
      const result = await getAdminEffectivePreview(selectedUserId)
      setPreview(result)
      toast.success('Effective access preview updated.')
    } catch (error: any) {
      toast.error('Failed to load effective preview')
    } finally {
      setLoading(false)
    }
  }

  const refreshHistory = async () => {
    if (!selectedUserId) return
    setLoading(true)
    try {
      const rows = await getAdminAccessHistory(selectedUserId, historyLimit)
      setHistory(rows)
      toast.success('Change history updated.')
    } catch (error: any) {
      toast.error('Failed to load access history')
    } finally {
      setLoading(false)
    }
  }

  const effectiveRows = useMemo(() => {
    const permissionMap = preview?.effectiveGraph?.permissions || {}
    return Object.entries(permissionMap).sort(([a], [b]) => a.localeCompare(b))
  }, [preview])

  const previewCatalogRows: PreviewCatalogRow[] = useMemo(() => {
    return effectiveRows.map(([permissionCode, decision]) => {
      const code = normalizeCode(permissionCode)
      const base =
        catalogByCode.get(code) ||
        ({
          permissionId: -1,
          permissionCode: code,
          displayName: null,
          moduleCategory: null,
          description: null,
          sortOrder: null,
        } as PermissionCatalogEntry)
      const resolved = resolveCatalogEntry({...base, permissionCode: code})
      return {...resolved, decision}
    })
  }, [effectiveRows, catalogByCode])

  const onTargetUserChange = (userId: number | null) => {
    setSelectedUserId(userId)
    const next = new URLSearchParams(searchParams)
    if (userId != null) {
      next.set('userId', String(userId))
    } else {
      next.delete('userId')
    }
    setSearchParams(next, { replace: true })
  }

  return (
    <div className='admin-access-management text-gray-800'>
      <ToastContainer />

      {createRoleOpen && canCreateCustomRole && (
        <div
          className='modal d-block'
          style={{ backgroundColor: 'rgba(0,0,0,0.35)', zIndex: 2000 }}
          role='dialog'
          aria-modal='true'
        >
          <div className='modal-dialog modal-dialog-centered modal-xl'>
            <div className='modal-content admin-access-role-catalog-modal'>
              <div className='modal-header flex-wrap align-items-center gap-2'>
                <div className='d-flex align-items-center gap-2 flex-wrap'>
                  <h5 className='modal-title mb-0'>Create role</h5>
                  <span className='badge badge-light text-gray-800'>Step {createRoleStep} of 4</span>
                </div>
                <button
                  type='button'
                  className='btn-close ms-auto'
                  aria-label='Close'
                  onClick={() => {
                    if (createRoleBusy) return
                    setCreateRoleOpen(false)
                    setCreateRoleStep(1)
                  }}
                />
              </div>
              <CreateRoleWizardBody
                createRoleStep={createRoleStep}
                crPresetCopyFromId={crPresetCopyFromId}
                rolesList={rolesList}
                crDisplayName={crDisplayName}
                setCrDisplayName={setCrDisplayName}
                crDescription={crDescription}
                setCrDescription={setCrDescription}
                crActive={crActive}
                setCrActive={setCrActive}
                isPlatformSuperAdminUser={isPlatformSuperAdminUser}
                crCompanyGroupAdminId={crCompanyGroupAdminId}
                setCrCompanyGroupAdminId={setCrCompanyGroupAdminId}
                crCompanyGroupOptions={crCompanyGroupOptions}
                crCompanyGroupLoadError={crCompanyGroupLoadError}
                crShowAdvancedCreateFields={crShowAdvancedCreateFields}
                setCrShowAdvancedCreateFields={setCrShowAdvancedCreateFields}
                crCode={crCode}
                setCrCode={setCrCode}
                crScope={crScope}
                setCrScope={setCrScope}
                crBehaviorTemplate={crBehaviorTemplate}
                setCrBehaviorTemplate={setCrBehaviorTemplate}
                crPermMode={crPermMode}
                setCrPermMode={setCrPermMode}
                crCopyFromId={crCopyFromId}
                setCrCopyFromId={setCrCopyFromId}
                crWizardPermSearch={crWizardPermSearch}
                setCrWizardPermSearch={setCrWizardPermSearch}
                crManualSelected={crManualSelected}
                toggleCrWizardPermission={toggleCrWizardPermission}
                setCrManualMany={setCrManualMany}
                permissionCatalog={permissionCatalog}
                crAccessTemplateId={crAccessTemplateId}
                pickAccessTemplate={pickAccessTemplate}
                crModuleFeatures={crModuleFeatures}
                toggleCrModuleFeature={toggleCrModuleFeature}
                crWizardBundleId={crWizardBundleId}
                setCrWizardBundleId={setCrWizardBundleId}
                suggestedBundles={suggestedWizardBundles}
                crWizardExtraCodes={crWizardExtraCodes}
                toggleCrWizardExtraPermission={toggleCrWizardExtraPermission}
                setCrWizardExtraMany={setCrWizardExtraMany}
                crShowFineTuneExtras={crShowFineTuneExtras}
                setCrShowFineTuneExtras={setCrShowFineTuneExtras}
                wizardSimpleCodes={wizardSimpleCodes}
                wizardSubmitCodes={wizardSubmitCodes}
              />
              <div className='modal-footer flex-wrap gap-2'>
                <button
                  type='button'
                  className='btn btn-light'
                  disabled={createRoleBusy}
                  onClick={() => {
                    setCreateRoleOpen(false)
                    setCreateRoleStep(1)
                  }}
                >
                  Cancel
                </button>
                {createRoleStep > 1 && (
                  <button
                    type='button'
                    className='btn btn-light'
                    disabled={createRoleBusy}
                    onClick={goCreateRoleBack}
                  >
                    Back
                  </button>
                )}
                {createRoleStep < 4 ? (
                  <button
                    type='button'
                    className='btn btn-primary'
                    disabled={
                      createRoleBusy ||
                      (createRoleStep === 1 &&
                        ((isPlatformSuperAdminUser && crCompanyGroupAdminId == null) ||
                          (!isPlatformSuperAdminUser && actorCompanyGroupId == null)))
                    }
                    onClick={goCreateRoleNext}
                  >
                    {createRoleStep === 1
                      ? 'Next: Access template'
                      : createRoleStep === 2
                        ? 'Next: Feature access'
                        : 'Next: Review'}
                  </button>
                ) : (
                  <>
                    <button
                      type='button'
                      className='btn btn-primary'
                      disabled={
                        createRoleBusy ||
                        (isPlatformSuperAdminUser && crCompanyGroupAdminId == null) ||
                        (crShowAdvancedCreateFields && crPermMode === 'COPY' && !crCopyFromId)
                      }
                      onClick={() => submitCreateRoleFinal()}
                    >
                      {createRoleBusy ? 'Creating…' : 'Create role'}
                    </button>
                    <button
                      type='button'
                      className='btn btn-light-primary'
                      disabled={
                        createRoleBusy ||
                        (isPlatformSuperAdminUser && crCompanyGroupAdminId == null) ||
                        (crShowAdvancedCreateFields && crPermMode === 'COPY' && !crCopyFromId)
                      }
                      onClick={() => submitCreateRoleFinal({assignUsersAfter: true})}
                    >
                      {createRoleBusy ? 'Creating…' : 'Create role & assign users'}
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {viewRoleRow && (
        <div
          className='modal d-block'
          style={{ backgroundColor: 'rgba(0,0,0,0.35)', zIndex: 2000 }}
          role='dialog'
          aria-modal='true'
        >
          <div className='modal-dialog modal-dialog-centered'>
            <div className='modal-content admin-access-role-catalog-modal'>
              <div className='modal-header'>
                <h5 className='modal-title mb-0'>View role</h5>
                <button
                  type='button'
                  className='btn-close'
                  aria-label='Close'
                  onClick={() => setViewRoleRow(null)}
                />
              </div>
              <div className='modal-body'>
                <div className='d-flex flex-wrap align-items-center gap-2 mb-3'>
                  <span className={`fs-5 fw-bold text-gray-900 mb-0`}>
                    {(viewRoleRow.displayName || viewRoleRow.roleType || `Role ${viewRoleRow.id}`).replace(
                      /_/g,
                      ' '
                    )}
                  </span>
                  {viewRoleRow.systemRole ? (
                    <span className='badge badge-light-warning text-gray-800'>Protected</span>
                  ) : null}
                  <span className={manageRolesRowType(viewRoleRow).badgeClass}>
                    {manageRolesRowType(viewRoleRow).label}
                  </span>
                </div>
                {viewRoleRow.description ? (
                  <p className='text-gray-800 small' style={{ lineHeight: 1.55 }}>
                    {viewRoleRow.description}
                  </p>
                ) : null}
                <ul className='small text-gray-800 mb-0 ps-3' style={{ lineHeight: 1.6 }}>
                  <li>
                    <strong className='text-gray-900'>Company scope:</strong>{' '}
                    {manageRolesCompanyScopeLabel(viewRoleRow)}
                  </li>
                  <li>
                    <strong className='text-gray-900'>Status:</strong>{' '}
                    {viewRoleRow.active === false ? 'Inactive' : 'Active'}
                  </li>
                  <li>
                    <strong className='text-gray-900'>Permissions on role:</strong>{' '}
                    {permissionCountByRoleId.get(viewRoleRow.id) ?? 0}
                  </li>
                  <li>
                    <strong className='text-gray-900'>Users with this role (in your user list):</strong>{' '}
                    {usersCountByRoleId.get(viewRoleRow.id) ?? 0}
                  </li>
                </ul>
                {viewRoleRow.systemRole ? (
                  <p className='small text-muted mb-0 mt-3'>
                    This is a protected system role. Details you can change from here depend on your administrator
                    permissions; day-to-day access is managed in packages and overrides per user.
                  </p>
                ) : null}
              </div>
              <div className='modal-footer'>
                <button type='button' className='btn btn-light' onClick={() => setViewRoleRow(null)}>
                  Close
                </button>
                {canEditRoleCatalogRow(viewRoleRow) ? (
                  <button
                    type='button'
                    className='btn btn-primary'
                    onClick={() => {
                      const r = viewRoleRow
                      setViewRoleRow(null)
                      openEditManageRole(r)
                    }}
                  >
                    Edit role
                  </button>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      )}

      {editManageRole && canEditRoleCatalogRow(editManageRole) && (
        <div
          className='modal d-block'
          style={{ backgroundColor: 'rgba(0,0,0,0.35)', zIndex: 2000 }}
          role='dialog'
          aria-modal='true'
        >
          <div className='modal-dialog modal-dialog-centered'>
            <div className='modal-content admin-access-role-catalog-modal'>
              <div className='modal-header'>
                <h5 className='modal-title'>Edit role</h5>
                <button
                  type='button'
                  className='btn-close'
                  aria-label='Close'
                  onClick={() => !editMrBusy && setEditManageRole(null)}
                />
              </div>
              <div className='modal-body'>
                <div className='mb-2 small text-muted'>
                  #{editManageRole.id} · {(editManageRole.roleType || '').replace(/_/g, ' ')}
                  {editManageRole.systemRole ? ' · System role' : ''}
                </div>
                <div className='mb-3'>
                  <label className='form-label fw-bold'>Display name</label>
                  <input
                    className='form-control'
                    value={editMrName}
                    onChange={(e) => setEditMrName(e.target.value)}
                  />
                </div>
                <div className='mb-3'>
                  <label className='form-label fw-bold'>Description</label>
                  <textarea
                    className='form-control'
                    rows={2}
                    value={editMrDesc}
                    onChange={(e) => setEditMrDesc(e.target.value)}
                  />
                </div>
                {editManageRole.customRole && !isPlatformSuperAdminUser && (
                  <div className='mb-3'>
                    <button
                      type='button'
                      className='btn btn-light btn-sm'
                      onClick={() => setEditMrShowAdvanced((v) => !v)}
                    >
                      {editMrShowAdvanced ? 'Hide advanced settings' : 'Advanced settings (optional)'}
                    </button>
                    <div className='small text-muted mt-1'>
                      Role code, access behavior, and default data visibility — only change these if you know the impact.
                    </div>
                  </div>
                )}
                {editManageRole.customRole && (isPlatformSuperAdminUser || editMrShowAdvanced) && (
                  <>
                    <div className='mb-3'>
                      <label className='form-label fw-bold'>Role code</label>
                      <input
                        className='form-control font-monospace'
                        value={editMrCode}
                        onChange={(e) => setEditMrCode(e.target.value)}
                        placeholder='e.g. regional_company_admin'
                      />
                    </div>
                    <div className='mb-3'>
                      <label className='form-label fw-bold'>Access behavior</label>
                      <SearchableSelect
                        className='form-select'
                        value={editMrBehavior}
                        onChange={(e) => setEditMrBehavior(e.target.value)}
                      >
                        {BEHAVIOR_TEMPLATE_OPTIONS.map((t) => (
                          <option key={t} value={t}>
                            {t.replace(/_/g, ' ')}
                          </option>
                        ))}
                      </SearchableSelect>
                    </div>
                    <div className='mb-3'>
                      <label className='form-label fw-bold'>Default data visibility</label>
                      <SearchableSelect
                        className='form-select'
                        value={editMrScope}
                        onChange={(e) => setEditMrScope(e.target.value)}
                      >
                        {DATA_SCOPE_OPTIONS.map((s) => (
                          <option key={s} value={s}>
                            {s}
                          </option>
                        ))}
                      </SearchableSelect>
                    </div>
                  </>
                )}
                {!editManageRole.systemRole && (
                  <div className='form-check'>
                    <input
                      id='mr-active'
                      type='checkbox'
                      className='form-check-input'
                      checked={editMrActive}
                      onChange={(e) => setEditMrActive(e.target.checked)}
                    />
                    <label className='form-check-label' htmlFor='mr-active'>
                      Active
                    </label>
                  </div>
                )}
              </div>
              <div className='modal-footer'>
                <button
                  type='button'
                  className='btn btn-light'
                  disabled={editMrBusy}
                  onClick={() => setEditManageRole(null)}
                >
                  Cancel
                </button>
                <button
                  type='button'
                  className='btn btn-primary'
                  disabled={editMrBusy}
                  onClick={submitEditManageRole}
                >
                  {editMrBusy ? 'Saving…' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {copyPermsOpen && copyPermsTarget && canEditRoleCatalogRow(copyPermsTarget) && (
        <div
          className='modal d-block'
          style={{ backgroundColor: 'rgba(0,0,0,0.35)', zIndex: 2000 }}
          role='dialog'
          aria-modal='true'
        >
          <div className='modal-dialog modal-dialog-centered'>
            <div className='modal-content admin-access-role-catalog-modal'>
              <div className='modal-header'>
                <h5 className='modal-title'>Copy permissions from another role</h5>
                <button
                  type='button'
                  className='btn-close'
                  aria-label='Close'
                  onClick={() => !copyPermsBusy && setCopyPermsOpen(false)}
                />
              </div>
              <div className='modal-body'>
                <p className='small text-muted'>
                  Target:{' '}
                  <strong className='text-gray-900'>
                    {copyPermsTarget.displayName || copyPermsTarget.code} (#{copyPermsTarget.id})
                  </strong>
                  . This replaces all <strong>role_permissions</strong> and <strong>role_menu_acl</strong> rows for the
                  target with copies from the source.
                </p>
                <label className='form-label fw-bold'>Source role</label>
                <SearchableSelect
                  className='form-select'
                  value={copyPermsSourceId}
                  onChange={(e) => setCopyPermsSourceId(e.target.value)}
                >
                  <option value=''>— Select role —</option>
                  {rolesList
                    .filter((r) => r.id !== copyPermsTarget.id)
                    .map((r) => (
                      <option key={r.id} value={r.id}>
                        {(r.displayName || r.roleType || `Role ${r.id}`).replace(/_/g, ' ')}
                        {r.code ? ` · ${r.code}` : ''} · #{r.id}
                      </option>
                    ))}
                </SearchableSelect>
              </div>
              <div className='modal-footer'>
                <button
                  type='button'
                  className='btn btn-light'
                  disabled={copyPermsBusy}
                  onClick={() => setCopyPermsOpen(false)}
                >
                  Cancel
                </button>
                <button
                  type='button'
                  className='btn btn-primary'
                  disabled={copyPermsBusy || !copyPermsSourceId}
                  onClick={submitCopyPermissions}
                >
                  {copyPermsBusy ? 'Copying…' : 'Copy and replace'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className='card mb-5 shadow-sm border border-gray-300'>
        <div className='card-header border-0 border-bottom border-gray-200 bg-white pt-5 pb-4 d-flex flex-wrap align-items-center justify-content-between gap-2'>
          <div>
            <h1 className='fw-bolder text-gray-900 fs-2 mb-2' style={{ letterSpacing: '-0.02em' }}>
              Access Management
            </h1>
            <p className='text-gray-700 mb-0' style={{ maxWidth: '48rem', lineHeight: 1.55 }}>
              Create roles, assign users, and manage access packages from one place. Adjust{' '}
              <strong className='text-gray-900'>role permissions</strong>,{' '}
              <strong className='text-gray-900'>access packages</strong>,{' '}
              <strong className='text-gray-900'>access exceptions</strong>, and{' '}
              <strong className='text-gray-900'>maximum data visibility</strong> when you need to. Use audit history for
              a change trail.
            </p>
          </div>
          <button
            className='btn btn-sm btn-primary fw-semibold px-4'
            onClick={loadBase}
            disabled={loading}
            type='button'
          >
            Refresh lists
          </button>
        </div>
        <div className='card-body pt-4 pb-5 bg-body'>
          <div className='row g-4 g-lg-5 align-items-stretch'>
            <div className='col-lg-5'>
              <label className='form-label fw-bold text-gray-900 mb-2'>Who are you setting up?</label>
              <div className='row g-2 mb-2'>
                <div className='col-12 col-sm-6'>
                  <label className='form-label small text-gray-800 mb-1'>Filter by role</label>
                  <SearchableSelect
                    className='form-select form-select-sm text-gray-900 border-gray-400'
                    value={pickerRoleFilter}
                    onChange={(e) => setPickerRoleFilter(e.target.value)}
                  >
                    <option value='ALL'>All roles</option>
                    {roleTypesForFilter.map((r) => (
                      <option key={r} value={r}>
                        {r.replace(/_/g, ' ')}
                      </option>
                    ))}
                  </SearchableSelect>
                </div>
                <div className='col-12 col-sm-6'>
                  <label className='form-label small text-gray-800 mb-1'>Filter by company</label>
                  <SearchableSelect
                    className='form-select form-select-sm text-gray-900 border-gray-400'
                    value={pickerCompanyFilter}
                    onChange={(e) => setPickerCompanyFilter(e.target.value)}
                  >
                    <option value='ALL'>All companies</option>
                    {companyNamesForFilter.length > 0 && (
                      <option value='__NO_COMPANY__'>No company (e.g. global operator)</option>
                    )}
                    {companyNamesForFilter.map((c) => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                  </SearchableSelect>
                </div>
              </div>
              <Select<UserPickerOption, false>
                className='text-start'
                classNamePrefix='admin-access-user'
                styles={userSelectStyles}
                isClearable
                isSearchable
                isLoading={loading && users.length === 0}
                options={userPickerOptions}
                value={selectedUserPickerOption}
                placeholder='Search or pick a person…'
                noOptionsMessage={() => 'No people match the filters. Adjust filters or refresh lists.'}
                filterOption={(option, input) => {
                  if (!input) return true
                  const q = input.toLowerCase()
                  const u = option.data.user
                  const blob = [u.fullName, u.username, u.email, u.roleType, u.roleName, u.companyName]
                    .filter(Boolean)
                    .join(' ')
                    .toLowerCase()
                  return blob.includes(q)
                }}
                formatOptionLabel={(opt) => (
                  <div>
                    <div className='fw-bold text-gray-900' style={{ lineHeight: 1.3 }}>
                      {getUserPrimaryLabel(opt.user)}
                    </div>
                    <div className='small text-gray-600' style={{ lineHeight: 1.3 }}>
                      {getUserSecondaryLine(opt.user)}
                    </div>
                  </div>
                )}
                onChange={(opt: SingleValue<UserPickerOption>) => onTargetUserChange(opt?.value ?? null)}
              />
              <div className='text-gray-600 small mt-2'>
                {usersMatchingPickerFilters.length} of {users.length} people shown
                {userIdFromQuery != null && (
                  <>
                    {' '}
                    · Bookmark:{' '}
                    <code className='text-gray-900 bg-light px-1 rounded'>?userId={userIdFromQuery}</code>
                  </>
                )}
              </div>
            </div>
            <div className='col-lg-7'>
              {selectedUserId == null ? (
                <div
                  className='rounded-3 p-4 h-100 d-flex align-items-center border border-2 border-dashed'
                  style={{ borderColor: '#c5ccd6', backgroundColor: '#f0f2f5', color: '#3f4254' }}
                >
                  Select someone above to see their name, packages count, and data visibility.
                </div>
              ) : (
                <div className='row g-3'>
                  <div className='col-sm-4'>
                    <div
                      className='rounded-3 p-3 h-100 border shadow-sm'
                      style={{ backgroundColor: '#fff', borderColor: '#e4e6ef' }}
                    >
                      <div className='text-uppercase small fw-bold mb-2' style={{ color: '#5e6278', letterSpacing: '0.04em' }}>
                        Person
                      </div>
                      <div className='fw-bolder text-gray-900 fs-4 mb-0' style={{ lineHeight: 1.2 }}>
                        {selectedUser ? getUserPrimaryLabel(selectedUser) : '—'}
                      </div>
                      <div className='text-gray-700 small mt-1'>
                        {selectedUser ? getUserSecondaryLine(selectedUser) : '—'}
                      </div>
                    </div>
                  </div>
                  <div className='col-sm-4'>
                    <div
                      className='rounded-3 p-3 h-100 border border-primary border-2 shadow-sm'
                      style={{ backgroundColor: '#f1f8ff' }}
                    >
                      <div className='text-uppercase small fw-bold mb-2' style={{ color: '#1b4f72', letterSpacing: '0.04em' }}>
                        Packages assigned
                      </div>
                      <div className='fw-bolder text-primary fs-1 lh-1 mb-0' style={{ fontSize: '2.25rem' }}>
                        {assignedPackageCount}
                      </div>
                      <div className='text-gray-800 small mt-2 fw-medium'>Active for this person</div>
                    </div>
                  </div>
                  <div className='col-sm-4'>
                    <div
                      className='rounded-3 p-3 h-100 border shadow-sm'
                      style={{ backgroundColor: '#fff', borderColor: '#e4e6ef' }}
                    >
                      <div className='text-uppercase small fw-bold mb-2' style={{ color: '#5e6278', letterSpacing: '0.04em' }}>
                        Data visibility summary
                      </div>
                      <div className='fw-bold text-gray-900 text-break' style={{ fontSize: '0.95rem', lineHeight: 1.4 }}>
                        {derivedDataScope.summary}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className='card shadow-sm border border-gray-300'>
        <div className='card-header border-0 flex-column align-items-stretch pb-0 pt-6 bg-white'>
          <div
            className='mb-3 p-3 p-md-4 rounded-3 border'
            style={{ borderColor: '#0d6efd', backgroundColor: 'rgba(13, 110, 253, 0.06)' }}
          >
            <div className='text-gray-900 fw-bold text-uppercase d-block mb-3 small' style={{ letterSpacing: '0.06em' }}>
              Choose a section
            </div>
            <ul className='nav nav-tabs border-bottom border-gray-300 flex-nowrap flex-sm-wrap' style={{ gap: '0.15rem' }}>
              {PRIMARY_SECTION_ORDER.map((sectionId) => {
                const meta = PRIMARY_SECTION_META[sectionId]
                const isActive = primarySectionActive === sectionId
                const firstTab = meta.tabs[0]
                return (
                  <li className='nav-item' key={sectionId}>
                    <button
                      className='nav-link py-3 px-2 px-sm-3 border-0 rounded-0'
                      onClick={() => setTab(firstTab)}
                      type='button'
                      style={{
                        color: isActive ? '#0d6efd' : '#3f4254',
                        fontWeight: isActive ? 700 : 500,
                        borderBottom: isActive ? '3px solid #0d6efd' : '3px solid transparent',
                        marginBottom: '-1px',
                        background: 'transparent',
                      }}
                      title={meta.help}
                    >
                      {meta.label}
                    </button>
                  </li>
                )
              })}
            </ul>
            {primarySectionActive ? (
              <>
                <p className='text-gray-800 small mt-3 mb-2' style={{ lineHeight: 1.55 }}>
                  {PRIMARY_SECTION_META[primarySectionActive].help}
                </p>
                {PRIMARY_SECTION_META[primarySectionActive].tabs.length > 1 ? (
                  <ul className='nav nav-pills flex-wrap gap-2 mb-0 pb-2'>
                    {PRIMARY_SECTION_META[primarySectionActive].tabs.map((tabKey) => {
                      const isSub = activeTab === tabKey
                      const subLabel = SUBTAB_LABELS[tabKey] ?? TAB_LABELS[tabKey]
                      return (
                        <li className='nav-item' key={tabKey}>
                          <button
                            className={`btn btn-sm fw-semibold ${isSub ? 'btn-primary' : 'btn-light border'}`}
                            onClick={() => setTab(tabKey)}
                            type='button'
                          >
                            {subLabel}
                          </button>
                        </li>
                      )
                    })}
                  </ul>
                ) : null}
              </>
            ) : null}
            {advancedToolsActive ? (
              <p className='text-gray-800 small mt-3 mb-0 p-2 rounded' style={{ backgroundColor: 'rgba(255, 255, 255, 0.7)' }}>
                You are viewing <strong>Advanced tools</strong>. Use the main sections above for day-to-day role and user
                access.
              </p>
            ) : null}
          </div>
          {showAdvancedToolsSection ? (
          <details
            className='mb-0 p-3 p-md-4 mt-1 rounded-3 border border-2'
            style={{ borderColor: '#ced4da', backgroundColor: '#e9ecef' }}
            open={advancedToolsExpanded}
            onToggle={(e) => {
              setAdvancedToolsExpanded((e.currentTarget as HTMLDetailsElement).open)
            }}
          >
            <summary
              className='text-gray-800 fw-bold text-uppercase small mb-0'
              style={{ letterSpacing: '0.05em', cursor: 'pointer' }}
            >
              Advanced tools
            </summary>
            <p className='text-gray-800 small mt-2 mb-3' style={{ lineHeight: 1.55 }}>
              Use these only for technical setup or troubleshooting.
              {!isPlatformSuperAdminUser ? (
                <>
                  {' '}
                  Shortcuts match what your account is allowed to open; day-to-day work stays under the sections above.
                </>
              ) : null}
            </p>
            <ul className='nav nav-pills flex-wrap gap-2 mb-0'>
              {advancedToolDeckItems.map(({ tab, label }) => {
                const isAdActive = activeTab === tab
                return (
                  <li className='nav-item' key={`adv-${tab}`}>
                    <button
                      className={`btn btn-sm fw-semibold ${isAdActive ? 'btn-primary' : 'btn-light'}`}
                      onClick={() => setTab(tab)}
                      type='button'
                      style={
                        isAdActive
                          ? { boxShadow: '0 1px 3px rgba(0,0,0,0.12)' }
                          : { color: '#3f4254', border: '1px solid #ced4da', backgroundColor: '#fff' }
                      }
                    >
                      {label}
                    </button>
                  </li>
                )
              })}
            </ul>
          </details>
          ) : null}
        </div>
        <div className='card-body pt-4 pb-6 bg-body'>
          {sectionHelp && (
            <div
              className='mb-4 p-3 p-md-4 rounded-3 border'
              style={{ borderColor: '#dee2e6', backgroundColor: '#f8f9fa' }}
            >
              {activeTab === 'manage-roles' ? (
                <div className='d-flex flex-column gap-3'>
                  <div className='d-flex flex-wrap align-items-start justify-content-between gap-3'>
                    <div className='flex-grow-1' style={{ minWidth: '220px' }}>
                      <div className='fw-bold text-gray-900 fs-5 mb-2'>{sectionHelp.title}</div>
                      <p className='text-gray-800 small mb-0' style={{ lineHeight: 1.55 }}>
                        {sectionHelp.body}
                      </p>
                    </div>
                  </div>
                  <div className='d-flex flex-wrap gap-2 align-items-center'>
                    {canCreateCustomRole && (
                      <button
                        type='button'
                        className='btn btn-primary fw-semibold'
                        onClick={openCreateRoleModal}
                      >
                        + Create role
                      </button>
                    )}
                    <button
                      type='button'
                      className='btn btn-primary fw-semibold'
                      onClick={goTopAssignUserToRole}
                    >
                      Assign role to user
                    </button>
                    <button type='button' className='btn btn-light border fw-semibold' onClick={openTopManagePermissions}>
                      Manage permissions
                    </button>
                    <button
                      type='button'
                      className='btn btn-light border fw-semibold'
                      onClick={openTopDuplicateRole}
                    >
                      Duplicate role
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <div className='fw-bold text-gray-900 fs-5 mb-2'>{sectionHelp.title}</div>
                  <p className='text-gray-800 small mb-0' style={{ lineHeight: 1.55 }}>
                    {sectionHelp.body}
                  </p>
                </>
              )}
            </div>
          )}
          {activeTab === 'role-defaults' && (
            <>
              <div
                className='alert alert-light border mb-4 py-3 px-3 small text-gray-800'
                style={{ lineHeight: 1.55 }}
              >
                <strong className='text-gray-900'>Simple permissions</strong> is the default. Use{' '}
                <strong className='text-gray-900'>Fine tune permissions</strong> for grouped checklist rows (sections start
                collapsed). Use <strong className='text-gray-900'>Advanced matrix</strong> when you need all groups
                expanded at once. All views update the same role and use the server permission catalog.
              </div>
              <div className='mb-4 row g-3 align-items-end'>
                <div className='col-md-5 col-lg-4'>
                  <label className='form-label fw-bold text-gray-900'>Role</label>
                  <div className='d-flex flex-wrap gap-2 align-items-stretch'>
                    <SearchableSelect
                      className='form-select text-gray-900 border-gray-400 flex-grow-1'
                      style={{ minWidth: '220px' }}
                      value={roleMatrixRoleId ?? ''}
                      disabled={editableRoleTargets.length === 0}
                      onChange={(e) =>
                        setRoleMatrixRoleId(e.target.value ? Number(e.target.value) : null)
                      }
                    >
                      {editableRoleTargets.length === 0 ? (
                        <option value=''>No editable roles</option>
                      ) : editableRoleTargets.map((r) => (
                        <option key={r.id} value={r.id}>
                          {(r.displayName || r.roleType || `Role ${r.id}`).replace(/_/g, ' ')}
                          {r.code ? ` (${r.code})` : ''} #{r.id}
                          {r.active === false ? ' — inactive' : ''}
                        </option>
                      ))}
                    </SearchableSelect>
                  </div>
                </div>
                <div className='col-md-5 col-lg-4'>
                  <label className='form-label fw-bold text-gray-900'>Search</label>
                  <input
                    className='form-control text-gray-900 border-gray-400'
                    placeholder='Module, name, or description…'
                    value={roleMatrixSearch}
                    onChange={(e) => setRoleMatrixSearch(e.target.value)}
                  />
                </div>
                <div className='col-md-12 col-lg-4'>
                  <label className='form-label fw-bold text-gray-900 d-block'>View</label>
                  <div className='d-flex flex-wrap gap-2'>
                    <button
                      type='button'
                      className={`btn btn-sm fw-semibold ${
                        roleMatrixUiMode === 'simple' ? 'btn-primary' : 'btn-light border'
                      }`}
                      onClick={() => setRoleMatrixUiMode('simple')}
                    >
                      Simple permissions
                    </button>
                    <button
                      type='button'
                      className={`btn btn-sm fw-semibold ${
                        roleMatrixUiMode === 'fine' ? 'btn-primary' : 'btn-light border'
                      }`}
                      onClick={() => setRoleMatrixUiMode('fine')}
                    >
                      Fine tune permissions
                    </button>
                    <button
                      type='button'
                      className={`btn btn-sm fw-semibold ${
                        roleMatrixUiMode === 'advanced' ? 'btn-primary' : 'btn-light border'
                      }`}
                      onClick={() => setRoleMatrixUiMode('advanced')}
                    >
                      Advanced matrix
                    </button>
                  </div>
                </div>
              </div>

              {!selectedMatrixRole || editableRoleTargets.length === 0 ? null : (
                <div className='row g-3 mb-4'>
                  <div className='col-6 col-xl-3'>
                    <div className='border rounded-3 p-3 h-100 bg-white shadow-sm'>
                      <div className='small text-muted text-uppercase fw-bold mb-1' style={{ letterSpacing: '0.04em' }}>
                        Modules with access
                      </div>
                      <div className='d-flex align-items-baseline gap-2 flex-wrap'>
                        <span className='fs-2 fw-bold text-gray-900'>{roleMatrixModulesWithSelection}</span>
                        <span className='small text-muted'>of {roleMatrixSimpleBuckets.length} shown</span>
                      </div>
                    </div>
                  </div>
                  <div className='col-6 col-xl-3'>
                    <div className='border rounded-3 p-3 h-100 bg-white shadow-sm'>
                      <div className='small text-muted text-uppercase fw-bold mb-1' style={{ letterSpacing: '0.04em' }}>
                        Permissions selected
                      </div>
                      <div className='d-flex align-items-baseline gap-2 flex-wrap'>
                        <span className='fs-2 fw-bold text-gray-900'>{roleDefaultDraft.size}</span>
                        <span className='small text-muted'>of {permissionCatalog.length} in catalog</span>
                      </div>
                    </div>
                  </div>
                  <div className='col-6 col-xl-3'>
                    <div className='border rounded-3 p-3 h-100 bg-white shadow-sm'>
                      <div className='small text-muted text-uppercase fw-bold mb-1' style={{ letterSpacing: '0.04em' }}>
                        Role scope
                      </div>
                      <div className='fw-semibold text-gray-900'>{roleMatrixScopeSummary(selectedMatrixRole)}</div>
                    </div>
                  </div>
                  <div className='col-6 col-xl-3'>
                    <div className='border rounded-3 p-3 h-100 bg-white shadow-sm'>
                      <div className='small text-muted text-uppercase fw-bold mb-1' style={{ letterSpacing: '0.04em' }}>
                        Role kind
                      </div>
                      {selectedMatrixRole.systemRole ? (
                        <span className='badge badge-light-warning text-gray-900'>Protected / system</span>
                      ) : selectedMatrixRole.customRole ? (
                        <span className='badge badge-light-primary text-gray-900'>Custom role</span>
                      ) : (
                        <span className='badge badge-light text-gray-900'>Standard</span>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {roleMatrixUiDisabled ? (
                <div className='alert alert-secondary border mb-3 small text-gray-800'>
                  {customMatrixLocked
                    ? 'This custom role is global or owned by another company — only a platform super administrator can change its permissions here. You can still review settings.'
                    : 'Protected system role: read-only for your account. You can review permissions but not save changes.'}
                </div>
              ) : null}

              {selectedMatrixRole?.active === false ? (
                <div className='alert alert-warning border mb-3 small text-gray-800'>
                  This role is inactive. You can still update its default permissions; assigned users will see the
                  updates when the role is active again.
                </div>
              ) : null}

              {roleDefaultDraft.size === 0 && selectedMatrixRole && editableRoleTargets.length > 0 ? (
                <div className='alert alert-warning border mb-3 small text-gray-800'>
                  <strong className='text-gray-900'>No permissions selected.</strong> This role would not grant catalog
                  access until you enable at least one permission.
                </div>
              ) : null}

              {editableRoleTargets.length === 0 ? (
                <div className='alert alert-light border text-gray-800'>
                  No editable lower roles are available in your company group.
                </div>
              ) : (
                <>
                  {roleMatrixUiMode === 'fine' ? (
                    <p className='small text-muted mb-3' style={{ lineHeight: 1.55 }}>
                      <strong className='text-gray-900'>Fine tune permissions:</strong> open a module group to adjust
                      individual catalog rows. Changes stay in sync with Simple permissions.
                    </p>
                  ) : null}
                  {roleMatrixUiMode === 'advanced' ? (
                    <p className='small text-muted mb-3' style={{ lineHeight: 1.55 }}>
                      <strong className='text-gray-900'>Advanced matrix:</strong> all groups start expanded. Use this for
                      fast bulk review; it is the same data as the other views.
                    </p>
                  ) : null}
                  {roleMatrixUiMode === 'simple' ? (
                    <SimpleRolePermissionPanel
                      catalog={permissionCatalog}
                      searchQuery={roleMatrixSearch}
                      selectedCodes={roleDefaultDraft}
                      disabled={roleMatrixUiDisabled}
                      onToggle={toggleRoleMatrixPermission}
                      onSetMany={setRoleMatrixMany}
                    />
                  ) : (
                    <div className='border rounded'>
                      <GroupedPermissionCheckboxGrid
                        catalog={permissionCatalog}
                        searchQuery={roleMatrixSearch}
                        selectedCodes={roleDefaultDraft}
                        disabled={roleMatrixUiDisabled}
                        onToggle={toggleRoleMatrixPermission}
                        emptyMessage='No permissions match your filter.'
                        countLegend='on role'
                        startAllExpanded={roleMatrixUiMode === 'advanced'}
                        bulk={{
                          totalCatalogCount: permissionCatalog.length,
                          onSetMany: setRoleMatrixMany,
                          disabled: roleMatrixUiDisabled,
                          globalLegend: 'on role',
                        }}
                      />
                    </div>
                  )}
                  <div className='d-flex flex-wrap gap-2 mt-4'>
                    <button
                      type='button'
                      className='btn btn-primary fw-semibold px-4'
                      disabled={loading || roleMatrixRoleId == null || roleMatrixUiDisabled}
                      onClick={saveRoleMatrix}
                    >
                      Save role permissions
                    </button>
                    <button
                      type='button'
                      className='btn btn-light btn-active-light-primary'
                      disabled={loading}
                      onClick={reloadRolePermissionsOnly}
                    >
                      Reload permissions
                    </button>
                  </div>
                </>
              )}
            </>
          )}
          {activeTab === 'registry' && (
            <>
              <div className='mb-4 d-flex justify-content-between align-items-center flex-wrap gap-2'>
                <input
                  className='form-control w-300px'
                  placeholder='Search code, display name, description, module…'
                  value={permissionSearch}
                  onChange={(e) => setPermissionSearch(e.target.value)}
                />
                <span className='badge badge-light'>{permissionCatalog.length} in catalog</span>
              </div>
              <div className='border rounded bg-body'>
                <GroupedPermissionCatalogReadonly catalog={permissionCatalog} searchQuery={permissionSearch} />
              </div>
            </>
          )}

          {activeTab === 'bundle-master' && (
            <div className='row g-6'>
              <div className='col-lg-7'>
                <div className='d-flex justify-content-between align-items-center mb-4'>
                  <h5 className='mb-0 text-gray-900 fw-bold'>Access packages</h5>
                  <input
                    className='form-control w-250px'
                    placeholder='Filter code/name'
                    value={bundleCodeFilter}
                    onChange={(e) => setBundleCodeFilter(e.target.value)}
                  />
                </div>
                <div className='table-responsive'>
                  <table className='table align-middle table-row-dashed'>
                    <thead>
                      <tr className='text-gray-900 fw-bold text-uppercase small'>
                        <th>Code</th>
                        <th>Name</th>
                        <th>Status</th>
                        <th>Type</th>
                        <th className='text-end'>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredBundles.map((bundle) => (
                        <tr key={bundle.id} className='text-gray-800'>
                          <td className='fw-semibold text-gray-900'>{bundle.code}</td>
                          <td>{bundle.name}</td>
                          <td>
                            <span className={`badge ${bundle.active ? 'badge-light-success' : 'badge-light-danger'}`}>
                              {bundle.active ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                          <td>
                            {bundle.systemDefined ? (
                              <span className='badge badge-light-warning text-gray-800'>System-defined</span>
                            ) : (
                              <span className='badge badge-light-primary text-gray-800'>Custom</span>
                            )}
                          </td>
                          <td className='text-end'>
                            <button
                              className='btn btn-sm btn-light-primary me-2'
                              onClick={() => editBundle(bundle)}
                            >
                              Edit
                            </button>
                            {!bundle.systemDefined && bundle.active && (
                              <button
                                className='btn btn-sm btn-light-danger'
                                onClick={() => deactivateBundle(bundle)}
                              >
                                Deactivate
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              <div className='col-lg-5'>
                <h5 className='mb-4 text-gray-900 fw-bold'>{bundleForm.id ? 'Edit package' : 'New package'}</h5>
                <div className='mb-3'>
                  <label className='form-label fw-bold text-gray-900'>Code</label>
                  <input
                    className='form-control text-gray-900 border-gray-400'
                    value={bundleForm.code}
                    onChange={(e) => setBundleForm((prev) => ({...prev, code: e.target.value}))}
                  />
                </div>
                <div className='mb-3'>
                  <label className='form-label fw-bold text-gray-900'>Name</label>
                  <input
                    className='form-control text-gray-900 border-gray-400'
                    value={bundleForm.name}
                    onChange={(e) => setBundleForm((prev) => ({...prev, name: e.target.value}))}
                  />
                </div>
                <div className='mb-3'>
                  <label className='form-label fw-bold text-gray-900'>Module hint (optional)</label>
                  <input
                    className='form-control text-gray-900 border-gray-400'
                    value={bundleForm.moduleHint}
                    onChange={(e) => setBundleForm((prev) => ({...prev, moduleHint: e.target.value}))}
                  />
                </div>
                <div className='form-check form-check-solid mb-5'>
                  <input
                    id='bundle-active'
                    type='checkbox'
                    className='form-check-input'
                    checked={bundleForm.active}
                    onChange={(e) => setBundleForm((prev) => ({...prev, active: e.target.checked}))}
                  />
                  <label htmlFor='bundle-active' className='form-check-label text-gray-900 fw-medium'>
                    Active
                  </label>
                </div>
                <div className='d-flex gap-2'>
                  <button
                    className='btn btn-primary fw-semibold px-4'
                    onClick={submitBundle}
                    disabled={loading}
                    type='button'
                  >
                    {bundleForm.id ? 'Save changes' : 'Create package'}
                  </button>
                  <button className='btn btn-light' onClick={resetBundleForm}>
                    Reset
                  </button>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'bundle-mapping' && (
            <>
              <div className='row mb-4 g-4'>
                <div className='col-md-6'>
                  <label className='form-label fw-bold text-gray-900'>Package to edit</label>
                  <SearchableSelect
                    className='form-select text-gray-900 border-gray-400'
                    value={selectedBundleId ?? ''}
                    onChange={(e) => setSelectedBundleId(e.target.value ? Number(e.target.value) : null)}
                  >
                    <option value=''>— Choose a package —</option>
                    {bundles.map((bundle) => (
                      <option key={bundle.id} value={bundle.id}>
                        {bundle.code} - {bundle.name}
                        {!bundle.active ? ' (Inactive)' : ''}
                      </option>
                    ))}
                  </SearchableSelect>
                </div>
                <div className='col-md-6 d-flex align-items-end justify-content-end gap-2'>
                  {selectedBundle?.systemDefined && (
                    <span className='badge badge-light-warning'>This package is locked (built-in)</span>
                  )}
                  <button
                    className='btn btn-primary fw-semibold px-4'
                    disabled={!selectedBundle || selectedBundle.systemDefined || loading}
                    onClick={saveBundlePermissionMapping}
                    type='button'
                  >
                    Update package permissions
                  </button>
                </div>
              </div>
              {selectedBundle && !selectedBundle.systemDefined && (
                <p className='text-gray-700 small mb-3'>
                  <strong className='text-gray-900'>Save</strong> replaces the stored permission set for this package with
                  every permission you keep checked (across the full catalog), not only rows visible below. Unchecking
                  removes a permission from the package even if you are in scoped view.
                </p>
              )}
              {selectedBundle && (
                <div className='mb-3'>
                  <div className='fw-bold text-gray-900 fs-6'>
                    Permissions in {selectedBundle.code} package
                  </div>
                  <div className='small text-muted'>
                    {mappedPermissionByCode.size} permission(s) in this package
                    {selectedBundle.systemDefined
                      ? ' · built-in package'
                      : packageSetupShowAllCatalog
                        ? ' · showing full catalog'
                        : ' · showing only permissions currently in this package'}
                  </div>
                </div>
              )}
              {selectedBundle && !selectedBundle.systemDefined && (
                <div className='form-check mb-3'>
                  <input
                    id='pkg-show-all-perms'
                    className='form-check-input'
                    type='checkbox'
                    checked={packageSetupShowAllCatalog}
                    onChange={(e) => setPackageSetupShowAllCatalog(e.target.checked)}
                  />
                  <label className='form-check-label fw-semibold text-gray-900' htmlFor='pkg-show-all-perms'>
                    Show all permissions to add (full catalog)
                  </label>
                  <div className='small text-muted ms-4 mb-0'>
                    Off by default: list is limited to this package so you can edit it without scanning the entire
                    catalog. Saving always uses the full set of checked permissions, including any not visible in your
                    current search.
                  </div>
                </div>
              )}
              <div className='row mb-3 g-3 align-items-end'>
                <div className='col-md-8'>
                  <label className='form-label fw-bold text-gray-900'>Search {selectedBundle ? 'in list' : 'permissions'}</label>
                  <input
                    className='form-control text-gray-900 border-gray-400'
                    placeholder='Search code, display name, description, module…'
                    value={bundleMapSearch}
                    onChange={(e) => setBundleMapSearch(e.target.value)}
                  />
                </div>
              </div>
              <div className='border rounded bg-white'>
                <GroupedBundlePermissionEditor
                  catalog={bundlePackageEditCatalog}
                  searchQuery={bundleMapSearch}
                  packageCountMode={bundlePackageCountMode}
                  emptyMessage={bundlePackageEmptyMessage}
                  packageBulk={{
                    totalCatalogCount: bundlePackageEditCatalog.length,
                    selectedCount: mappedPermissionByCode.size,
                    onSetManyInPackage: setBundlePermissionsMany,
                    disabled:
                      loading ||
                      !selectedBundle ||
                      Boolean(selectedBundle.systemDefined),
                  }}
                  getMapped={(code) => {
                    const key = normalizeCode(code)
                    const row = mappedPermissionByCode.get(key)
                    if (!row) return undefined
                    return {
                      permissionCode: key,
                      effect: row.effect,
                      defaultScope: row.defaultScope,
                    }
                  }}
                  selectedBundleOk={Boolean(selectedBundle)}
                  systemLocked={Boolean(selectedBundle?.systemDefined)}
                  onToggleInBundle={togglePermissionInBundle}
                  onEffect={setPermissionEffect}
                  onScope={setPermissionScope}
                />
              </div>
            </>
          )}

          {activeTab === 'manage-roles' && (
            <>
              <div className='alert alert-light border text-gray-800 mb-3'>
                {isPlatformSuperAdminUser
                  ? 'Super administrator: full role catalog. Use the optional API filter companyGroupAdminId when integrating other tools.'
                  : actorCompanyGroupId != null
                    ? 'Company scope: you see global roles and custom roles for your organization. You can create and edit only your company’s custom roles; system and global custom roles are view-only for metadata changes.'
                    : 'You see global roles only. A company group context is required to create or edit company-owned custom roles.'}
              </div>
              <div className='table-responsive border rounded'>
                <table className='table align-middle table-row-dashed table-sm mb-0'>
                  <thead>
                    <tr className='text-gray-900 fw-bold text-uppercase small'>
                      <th>Role name</th>
                      <th>Type</th>
                      <th>Company scope</th>
                      <th>Status</th>
                      <th className='text-end'>Users</th>
                      <th className='text-end'>Permissions</th>
                      <th className='text-end'>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rolesList.length === 0 ? (
                      <tr>
                        <td colSpan={7} className='text-center text-gray-700 py-6'>
                          No roles loaded.
                        </td>
                      </tr>
                    ) : (
                      [...rolesList]
                        .sort((a, b) => (a.id ?? 0) - (b.id ?? 0))
                        .map((r) => {
                          const rowType = manageRolesRowType(r)
                          return (
                            <tr key={r.id} className='text-gray-800'>
                              <td className='fw-semibold'>
                                {(r.displayName || r.roleType || `Role ${r.id}`).replace(/_/g, ' ')}
                                {r.systemRole ? (
                                  <span
                                    className='ms-1 badge badge-light-warning text-gray-800'
                                    title='Protected system role'
                                  >
                                    Locked
                                  </span>
                                ) : null}
                              </td>
                              <td>
                                <span className={rowType.badgeClass}>{rowType.label}</span>
                              </td>
                              <td className='small'>{manageRolesCompanyScopeLabel(r)}</td>
                              <td>
                                <span
                                  className={`badge ${r.active === false ? 'badge-light-danger' : 'badge-light-success'}`}
                                >
                                  {r.active === false ? 'Inactive' : 'Active'}
                                </span>
                              </td>
                              <td className='text-end small'>
                                <span className='fw-semibold'>{usersCountByRoleId.get(r.id) ?? '—'}</span>
                              </td>
                              <td className='text-end small'>
                                <span className='fw-semibold'>{permissionCountByRoleId.get(r.id) ?? 0}</span>
                              </td>
                              <td className='text-end'>
                                <div className='d-flex flex-wrap gap-1 justify-content-end'>
                                  <button
                                    type='button'
                                    className='btn btn-sm btn-light'
                                    onClick={() => openViewRoleRow(r)}
                                  >
                                    View
                                  </button>
                                  <button
                                    type='button'
                                    className='btn btn-sm btn-light-primary'
                                    disabled={!canEditRoleCatalogRow(r)}
                                    title={
                                      !canEditRoleCatalogRow(r)
                                        ? 'You can only edit custom roles owned by your company'
                                        : undefined
                                    }
                                    onClick={() => openEditManageRole(r)}
                                  >
                                    Edit
                                  </button>
                                  <button
                                    type='button'
                                    className='btn btn-sm btn-light-primary'
                                    disabled={!canEditRoleCatalogRow(r)}
                                    title={
                                      !canEditRoleCatalogRow(r)
                                        ? 'You can manage only lower roles within your company group'
                                        : undefined
                                    }
                                    onClick={() => goManagePermissionsForRole(r)}
                                  >
                                    Manage permissions
                                  </button>
                                  <button
                                    type='button'
                                    className='btn btn-sm btn-light-success text-gray-900'
                                    onClick={() => goAssignUsersForRole(r)}
                                  >
                                    Assign users
                                  </button>
                                  <button
                                    type='button'
                                    className='btn btn-sm btn-light-primary'
                                    disabled={!r.customRole || !canEditRoleCatalogRow(r)}
                                    title={
                                      !r.customRole
                                        ? 'Duplicate is only available for custom roles'
                                        : !canEditRoleCatalogRow(r)
                                          ? 'You can only duplicate your company’s custom roles'
                                          : undefined
                                    }
                                    onClick={() => openDuplicateRoleWizard(r)}
                                  >
                                    Duplicate
                                  </button>
                                  {r.customRole && (
                                    <button
                                      type='button'
                                      className='btn btn-sm btn-light'
                                      disabled={!canEditRoleCatalogRow(r)}
                                      title={
                                        !canEditRoleCatalogRow(r)
                                          ? 'You can only replace permissions for your company’s custom roles'
                                          : undefined
                                      }
                                      onClick={() => openCopyPermissionsModal(r)}
                                    >
                                      Copy from…
                                    </button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          )
                        })
                    )}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {activeTab === 'user-bundles' && (
            <>
              {!selectedUserId ? (
                <div
                  className='rounded-3 p-4 p-md-5 text-center border border-2 border-dashed fw-medium'
                  style={{ borderColor: '#c5ccd6', backgroundColor: '#f0f2f5', color: '#3f4254' }}
                >
                  Use <strong>Who are you setting up?</strong> at the top, then pick packages here.
                </div>
              ) : bundles.length === 0 ? (
                <div
                  className='rounded-3 p-4 p-md-5 text-center border border-2 border-dashed'
                  style={{ borderColor: '#c5ccd6', backgroundColor: '#f0f2f5', color: '#3f4254' }}
                >
                  No packages in the list yet. Open <strong className='text-gray-900'>Access packages</strong> in the main
                  navigation (or Advanced tools → Access packages) to create one, or ask an administrator to load standard
                  packages.
                </div>
              ) : (
                <>
              <p className='text-gray-800 small mb-3' style={{ lineHeight: 1.55 }}>
                Turn on what this person should have. Rows you cannot check are off until re-enabled under{' '}
                <strong className='text-gray-900'>Access packages</strong>.
              </p>
              <div className='table-responsive'>
                <table className='table align-middle table-row-dashed'>
                  <thead>
                    <tr className='text-gray-900 fw-bold text-uppercase small'>
                      <th className='w-50px'>On</th>
                      <th>Code</th>
                      <th>Package name</th>
                      <th>Status</th>
                      <th>Kind</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bundles.map((bundle) => {
                      const checked = assignedBundleIds.includes(bundle.id)
                      return (
                        <tr key={bundle.id} className='text-gray-800'>
                          <td>
                            <input
                              type='checkbox'
                              className='form-check-input'
                              checked={checked}
                              disabled={!bundle.active || !selectedUserId}
                              onChange={(e) => {
                                setAssignedBundleIds((prev) =>
                                  e.target.checked
                                    ? Array.from(new Set([...prev, bundle.id]))
                                    : prev.filter((id) => id !== bundle.id)
                                )
                              }}
                            />
                          </td>
                          <td className='fw-semibold'>{bundle.code}</td>
                          <td>{bundle.name}</td>
                          <td>
                            <span className={`badge ${bundle.active ? 'badge-light-success' : 'badge-light-danger'}`}>
                              {bundle.active ? 'Available' : 'Off'}
                            </span>
                          </td>
                          <td>{bundle.systemDefined ? 'Standard' : 'Custom'}</td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
                </>
              )}
              <button
                className='btn btn-primary fw-semibold px-4 mt-4'
                disabled={!selectedUserId || loading || bundles.length === 0}
                onClick={saveUserBundles}
                type='button'
              >
                Save package assignments
              </button>
            </>
          )}

          {activeTab === 'overrides' && (
            <>
              {!selectedUserId ? (
                <div
                  className='rounded-3 p-4 p-md-5 text-center border border-2 border-dashed fw-medium'
                  style={{ borderColor: '#c5ccd6', backgroundColor: '#f0f2f5', color: '#3f4254' }}
                >
                  Select a person at the top first. A <strong className='text-gray-900'>Deny</strong> in access exceptions
                  overrides package allowances.
                </div>
              ) : (
                <>
                  <p className='text-gray-800 small mb-3' style={{ lineHeight: 1.55 }}>
                    Adjust this person’s access exceptions without typing raw permission codes (unless you need to).{' '}
                    <strong className='text-gray-900'>Deny</strong> still wins over packages and allows.
                  </p>
                  <div className='btn-group mb-4' role='group'>
                    <button
                      type='button'
                      className={`btn btn-sm fw-semibold ${overrideUiMode === 'simple' ? 'btn-primary' : 'btn-light'}`}
                      onClick={() => setOverrideUiMode('simple')}
                    >
                      Simple builder
                    </button>
                    <button
                      type='button'
                      className={`btn btn-sm fw-semibold ${overrideUiMode === 'advanced' ? 'btn-primary' : 'btn-light'}`}
                      onClick={() => setOverrideUiMode('advanced')}
                    >
                      Advanced (raw codes)
                    </button>
                  </div>

                  {overrideUiMode === 'simple' && (
                    <>
                      <div className='mb-4'>
                        <div className='fw-bold text-gray-900 small text-uppercase mb-2'>
                          Common scenarios
                        </div>
                        <div className='d-flex flex-wrap gap-2'>
                          {OVERRIDE_RECIPES.map((recipe) => (
                            <button
                              key={recipe.id}
                              type='button'
                              className='btn btn-sm btn-light border text-gray-800'
                              title={recipe.description}
                              onClick={() => applyOverrideRecipe(recipe)}
                            >
                              {recipe.label}
                            </button>
                          ))}
                        </div>
                      </div>
                      <div className='row g-4 mb-4'>
                        <div className='col-md-6 col-lg-4'>
                          <label className='form-label fw-bold text-gray-900'>Module</label>
                          <SearchableSelect
                            className='form-select text-gray-900 border-gray-400'
                            value={simpleModule}
                            onChange={(e) => changeSimpleModule(e.target.value as OverrideModuleId)}
                          >
                            {OVERRIDE_MODULES.map((m) => (
                              <option key={m.id} value={m.id}>
                                {m.label}
                              </option>
                            ))}
                          </SearchableSelect>
                        </div>
                        <div className='col-md-6 col-lg-4'>
                          <label className='form-label fw-bold text-gray-900'>Area / menu focus</label>
                          <SearchableSelect
                            className='form-select text-gray-900 border-gray-400'
                            value={simpleFeature}
                            onChange={(e) => setSimpleFeature(e.target.value)}
                          >
                            {simpleModuleFeatures.map((f) => (
                              <option key={f.id} value={f.id}>
                                {f.label}
                              </option>
                            ))}
                          </SearchableSelect>
                        </div>
                        <div className='col-md-6 col-lg-4'>
                          <label className='form-label fw-bold text-gray-900'>Access preset</label>
                          <SearchableSelect
                            className='form-select text-gray-900 border-gray-400'
                            value={simplePreset}
                            onChange={(e) => setSimplePreset(e.target.value as OverridePresetId)}
                          >
                            {OVERRIDE_PRESETS.map((p) => (
                              <option key={p.id} value={p.id} title={p.description}>
                                {p.label}
                              </option>
                            ))}
                          </SearchableSelect>
                        </div>
                        <div className='col-md-6 col-lg-4'>
                          <label className='form-label fw-bold text-gray-900'>Reason (optional)</label>
                          <input
                            className='form-control text-gray-900 border-gray-400'
                            placeholder='e.g. Auditor read-only per IT-482'
                            value={simpleReason}
                            onChange={(e) => setSimpleReason(e.target.value)}
                          />
                        </div>
                        <div className='col-md-6 col-lg-4'>
                          <label className='form-label fw-bold text-gray-900'>Scope (optional)</label>
                          <SearchableSelect
                            className='form-select text-gray-900 border-gray-400'
                            value={simpleScope}
                            onChange={(e) => setSimpleScope((e.target.value || '') as AccessScope | '')}
                          >
                            <option value=''>Default / none</option>
                            <option value='ALL'>ALL</option>
                            <option value='COMPANY'>COMPANY</option>
                            <option value='VESSEL'>VESSEL</option>
                          </SearchableSelect>
                        </div>
                      </div>
                      <div
                        className='mb-4 p-3 p-md-4 rounded-3 border'
                        style={{ borderColor: '#dee2e6', backgroundColor: '#f8f9fa' }}
                      >
                        <div className='d-flex justify-content-between align-items-start flex-wrap gap-2 mb-2'>
                          <div className='fw-bold text-gray-900'>Preview (before add)</div>
                          <span className='badge badge-light-primary'>
                            {simpleBuilderPreview.scopeCodes.length} permission(s) in scope
                          </span>
                          <span className='badge badge-light-success'>
                            {simpleBuilderPreview.overrides.length} override row(s) to add
                          </span>
                        </div>
                        <p className='text-gray-700 small mb-2' style={{ lineHeight: 1.55 }}>
                          {OVERRIDE_PRESETS.find((p) => p.id === simplePreset)?.description}
                        </p>
                        <ul className='small text-gray-800 mb-0 ps-3'>
                          {simpleBuilderPreview.previewLines.length === 0 ? (
                            <li className='text-gray-600'>Nothing to preview yet.</li>
                          ) : (
                            simpleBuilderPreview.previewLines.map((line, i) => (
                              <li
                                key={i}
                                className={
                                  line.startsWith('Note:') ? 'text-warning fw-semibold' : ''
                                }
                              >
                                {line}
                              </li>
                            ))
                          )}
                        </ul>
                      </div>
                      <div className='d-flex flex-wrap gap-2 mb-5'>
                        <button
                          type='button'
                          className='btn btn-primary fw-semibold px-4'
                          disabled={loading || simpleBuilderPreview.overrides.length === 0}
                          onClick={addSimpleOverridesToList}
                        >
                          Add preview to override list
                        </button>
                        <span className='text-gray-700 small align-self-center'>
                          Current list: <strong className='text-gray-900'>{userOverrides.length}</strong> row(s) — review
                          in Advanced or save below.
                        </span>
                      </div>
                    </>
                  )}

                  {overrideUiMode === 'advanced' && (
                    <p className='text-gray-800 small mb-3' style={{ lineHeight: 1.55 }}>
                      Edit raw permission codes exactly as stored in the access catalog. Use only when Simple mode
                      does not cover your case.
                    </p>
                  )}

                  <div className='mb-3 row g-3 align-items-end'>
                    <div className='col-md-8'>
                      <label className='form-label fw-bold text-gray-900'>Search override list</label>
                      <input
                        className='form-control text-gray-900 border-gray-400'
                        placeholder='Search permission, module, description…'
                        value={overrideSearch}
                        onChange={(e) => setOverrideSearch(e.target.value)}
                      />
                    </div>
                  </div>
                  <div className='border rounded bg-white'>
                    <GroupedUserOverrideEditor
                      rows={userOverrides.map((row, index) => ({
                        index,
                        permissionCode: row.permissionCode,
                        effect: row.effect,
                        scope: row.scope ?? null,
                        reason: row.reason || '',
                      }))}
                      catalogEntries={permissionCatalog}
                      catalogByCode={catalogByCode}
                      searchQuery={overrideSearch}
                      advancedMode={overrideUiMode === 'advanced'}
                      onPermissionChange={(index, code) =>
                        setUserOverrides((prev) =>
                          prev.map((item, idx) =>
                            idx === index ? {...item, permissionCode: code ? normalizeCode(code) : ''} : item
                          )
                        )
                      }
                      onEffectChange={(index, effect) =>
                        setUserOverrides((prev) =>
                          prev.map((item, idx) => (idx === index ? {...item, effect} : item))
                        )
                      }
                      onScopeChange={(index, scope) =>
                        setUserOverrides((prev) =>
                          prev.map((item, idx) => (idx === index ? {...item, scope} : item))
                        )
                      }
                      onReasonChange={(index, reason) =>
                        setUserOverrides((prev) =>
                          prev.map((item, idx) => (idx === index ? {...item, reason} : item))
                        )
                      }
                      onRemove={(index) =>
                        setUserOverrides((prev) => prev.filter((_, idx) => idx !== index))
                      }
                      overrideModuleBulk={userOverrideModuleBulk}
                    />
                  </div>
                  <div className='d-flex flex-wrap gap-2 mt-4'>
                    <button
                      type='button'
                      className='btn btn-light-primary'
                      disabled={overrideUiMode === 'simple'}
                      title={
                        overrideUiMode === 'simple'
                          ? 'Switch to Advanced to add a row with a raw permission code'
                          : undefined
                      }
                      onClick={() =>
                        setUserOverrides((prev) => [
                          ...prev,
                          {permissionCode: '', effect: 'DENY', scope: null, reason: ''},
                        ])
                      }
                    >
                      Add empty row
                    </button>
                    <button
                      type='button'
                      className='btn btn-primary fw-semibold px-4'
                      disabled={!selectedUserId || loading}
                      onClick={saveUserOverrides}
                    >
                      Save exceptions
                    </button>
                  </div>
                </>
              )}
            </>
          )}

          {activeTab === 'scope' && (
            <>
              {!selectedUserId ? (
                <div
                  className='rounded-3 p-4 p-md-5 text-center border border-2 border-dashed fw-medium'
                  style={{ borderColor: '#c5ccd6', backgroundColor: '#f0f2f5', color: '#3f4254' }}
                >
                  Select a person at the top to configure maximum data visibility.
                </div>
              ) : scopeEdit == null ? (
                <div className='text-gray-700'>Loading scope…</div>
              ) : (
                <div
                  className='rounded-3 p-4 p-md-5 border'
                  style={{ borderColor: '#dee2e6', backgroundColor: '#f8f9fa' }}
                >
                  <h5 className='fw-bold text-gray-900 mb-3'>Maximum data visibility</h5>
                  <p className='text-gray-800 small mb-4' style={{ lineHeight: 1.65, maxWidth: '44rem' }}>
                    Stored in <code className='small'>user_scope_binding</code> and enforced on admin business APIs when
                    resource context is provided. Non–super-admins cannot assign broader scope than their own company
                    or group (see server validation).
                  </p>
                  <div className='row g-3 mb-4'>
                    <div className='col-md-4'>
                      <label className='form-label fw-bold text-gray-900'>Scope</label>
                      <SearchableSelect
                        className='form-select text-gray-900 border-gray-400'
                        value={scopeEdit.hardMaxScope}
                        onChange={(e) =>
                          setScopeEdit((s) =>
                            s ? {...s, hardMaxScope: e.target.value as AccessScope} : s
                          )
                        }
                      >
                        <option value='ALL'>ALL</option>
                        <option value='COMPANY'>COMPANY</option>
                        <option value='VESSEL'>VESSEL</option>
                      </SearchableSelect>
                    </div>
                    <div className='col-md-4'>
                      <label className='form-label fw-bold text-gray-900'>Company admin id</label>
                      <input
                        className='form-control text-gray-900 border-gray-400'
                        value={scopeEdit.companyId}
                        onChange={(e) =>
                          setScopeEdit((s) => (s ? {...s, companyId: e.target.value} : s))
                        }
                        disabled={scopeEdit.hardMaxScope !== 'COMPANY'}
                        placeholder='Required for COMPANY'
                      />
                    </div>
                    <div className='col-md-4'>
                      <label className='form-label fw-bold text-gray-900'>Vessel id</label>
                      <input
                        className='form-control text-gray-900 border-gray-400'
                        value={scopeEdit.vesselId}
                        onChange={(e) =>
                          setScopeEdit((s) => (s ? {...s, vesselId: e.target.value} : s))
                        }
                        disabled={scopeEdit.hardMaxScope !== 'VESSEL'}
                        placeholder='Required for VESSEL'
                      />
                    </div>
                  </div>
                  <div
                    className='mb-4 p-3 rounded-3 border bg-white'
                    style={{ borderColor: '#e4e6ef' }}
                  >
                    <div className='fw-bold text-gray-900 small mb-2'>Account-derived summary (reference)</div>
                    <div className='text-gray-800 small fw-semibold'>{derivedDataScope.summary}</div>
                    <p className='text-gray-600 small mb-0 mt-2' style={{ lineHeight: 1.55 }}>
                      {derivedDataScope.description}
                    </p>
                  </div>
                  <button
                    type='button'
                    className='btn btn-primary fw-semibold px-4'
                    disabled={loading}
                    onClick={saveUserScopeBinding}
                  >
                    Save data visibility
                  </button>
                </div>
              )}
            </>
          )}

          {activeTab === 'preview' && (
            <>
              <div className='d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2'>
                <div>
                  <h5 className='mb-1 text-gray-900 fw-bold'>Result for the selected person</h5>
                  <div className='text-gray-800 small' style={{ lineHeight: 1.5 }}>
                    <span className='me-2'>Access mode: <strong className='text-gray-900'>{preview?.mode || '—'}</strong></span>
                    <span className='d-none d-md-inline text-gray-500'>|</span>
                    <span className='ms-md-2 d-block d-md-inline'>Detail: {preview?.effectiveGraph?.resolutionMode || '—'}</span>
                  </div>
                </div>
                <button
                  className='btn btn-primary fw-semibold px-4'
                  onClick={refreshPreview}
                  disabled={!selectedUserId || loading}
                  type='button'
                >
                  Refresh
                </button>
              </div>
              <div className='mb-4 d-flex flex-wrap gap-2'>
                <span className='badge badge-light-info' title='Only in older permission system'>
                  Legacy-only items: {preview?.onlyInLegacy?.length || 0}
                </span>
                <span className='badge badge-light-warning' title='Only in the new access model'>
                  New model-only: {preview?.onlyInNew?.length || 0}
                </span>
                <span className='badge badge-light-success'>Rows shown: {effectiveRows.length}</span>
              </div>
              {selectedUserId && (
                <div
                  className='mb-4 p-3 rounded-3 border'
                  style={{ borderColor: '#dee2e6', backgroundColor: '#f8f9fa' }}
                >
                  <div className='fw-bold text-gray-900 small mb-2'>Packages merged into this preview</div>
                  <div className='text-gray-800 small mb-2' style={{ lineHeight: 1.55 }}>
                    <span className='fw-semibold text-gray-900'>Default (role/rank):</span>{' '}
                    {(preview?.effectiveGraph?.defaultBundleIds || []).length === 0 ? (
                      <span className='text-gray-600'>None</span>
                    ) : (
                      (preview?.effectiveGraph?.defaultBundleIds || []).map((id) => (
                        <span key={`def-${id}`} className='badge badge-light-primary me-1 mb-1'>
                          {bundleCodeById.get(id) ?? `#${id}`}
                        </span>
                      ))
                    )}
                  </div>
                  <div className='text-gray-800 small' style={{ lineHeight: 1.55 }}>
                    <span className='fw-semibold text-gray-900'>Assigned to user:</span>{' '}
                    {(preview?.effectiveGraph?.assignedBundleIds || []).length === 0 ? (
                      <span className='text-gray-600'>None</span>
                    ) : (
                      (preview?.effectiveGraph?.assignedBundleIds || []).map((id) => (
                        <span key={`asg-${id}`} className='badge badge-light-info text-gray-800 me-1 mb-1'>
                          {bundleCodeById.get(id) ?? `#${id}`}
                        </span>
                      ))
                    )}
                  </div>
                  <p className='text-gray-600 small mb-0 mt-2'>
                    Access exceptions (allow/deny) and visibility limits are applied after packages; deny always wins.
                  </p>
                </div>
              )}
              {!selectedUserId ? (
                <div
                  className='rounded-3 p-4 p-md-5 text-center border border-2 border-dashed fw-medium'
                  style={{ borderColor: '#c5ccd6', backgroundColor: '#f0f2f5', color: '#3f4254' }}
                >
                  Select a person at the top, set packages, then return here. Click <strong>Refresh</strong> to load the
                  list.
                </div>
              ) : effectiveRows.length === 0 ? (
                <div
                  className='rounded-3 p-4 p-md-5 text-center border border-2 border-dashed'
                  style={{ borderColor: '#c5ccd6', backgroundColor: '#f0f2f5', color: '#3f4254' }}
                >
                  Nothing to show yet. After packages or scope are set, click <strong>Refresh</strong>. In legacy-only
                  mode, the summary may stay limited.
                </div>
              ) : (
                <>
                  <div className='mb-3 row g-3 align-items-end'>
                    <div className='col-md-8'>
                      <label className='form-label fw-bold text-gray-900'>Search effective permissions</label>
                      <input
                        className='form-control text-gray-900 border-gray-400'
                        placeholder='Search code, display name, description, module, source…'
                        value={previewSearch}
                        onChange={(e) => setPreviewSearch(e.target.value)}
                      />
                    </div>
                  </div>
                  <p className='small text-gray-600 mb-2'>
                    Rows show <strong className='text-gray-900'>allow/deny</strong>, data reach, winning source, and path.
                  </p>
                  <div className='border rounded bg-white'>
                    <GroupedEffectiveAccessPanel rows={previewCatalogRows} searchQuery={previewSearch} />
                  </div>
                </>
              )}
            </>
          )}

          {activeTab === 'history' && (
            <>
              <p className='text-gray-800 small mb-3' style={{ lineHeight: 1.55 }}>
                Recent access saves for this person. “Before/after” holds technical detail for support if you need to
                audit.
              </p>
              <div className='d-flex justify-content-between align-items-end mb-4 flex-wrap gap-2'>
                <div className='w-200px'>
                  <label className='form-label fw-bold text-gray-900'>How many past events to load</label>
                  <input
                    type='number'
                    className='form-control text-gray-900 border-gray-400'
                    min={1}
                    max={500}
                    value={historyLimit}
                    onChange={(e) => setHistoryLimit(Number(e.target.value || 100))}
                  />
                </div>
                <button
                  className='btn btn-primary fw-semibold px-4'
                  onClick={refreshHistory}
                  disabled={!selectedUserId || loading}
                  type='button'
                >
                  Load / refresh
                </button>
              </div>
              {!selectedUserId ? (
                <div
                  className='rounded-3 p-4 p-md-5 text-center border border-2 border-dashed fw-medium'
                  style={{ borderColor: '#c5ccd6', backgroundColor: '#f0f2f5', color: '#3f4254' }}
                >
                  Select a person at the top to see their change history.
                </div>
              ) : history.length === 0 ? (
                <div
                  className='rounded-3 p-4 p-md-5 text-center border border-2 border-dashed'
                  style={{ borderColor: '#c5ccd6', backgroundColor: '#f0f2f5', color: '#3f4254' }}
                >
                  No access changes on file for this person yet.
                </div>
              ) : (
              <div className='table-responsive'>
                <table className='table align-middle table-row-dashed'>
                  <thead>
                    <tr className='text-gray-900 fw-bold text-uppercase small'>
                      <th>When</th>
                      <th>What changed</th>
                      <th>By user id</th>
                      <th>Target user</th>
                      <th>Before (detail)</th>
                      <th>After (detail)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {history.map((row) => (
                      <tr key={row.id}>
                        <td>{prettyDate(row.createdAt)}</td>
                        <td>{row.changeType}</td>
                        <td>{row.actorUserId ?? '—'}</td>
                        <td>{row.targetUserId ?? '—'}</td>
                        <td>
                          <code className='text-gray-800 small'>{row.beforeJson ? 'Saved' : '—'}</code>
                        </td>
                        <td>
                          <code className='text-gray-800 small'>{row.afterJson ? 'Saved' : '—'}</code>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  )
}

export default AdminAccessManagement
